/**
 * Sync engine — core reconciliation orchestrator.
 *
 * 1. Reads the persisted cursors from Dexie `meta` store.
 *    - `syncCursor` — delta sync cursor (stream ID from toCursor).
 *    - `lastEventCursor` — timestamp for `since` in fetchEvents.
 * 2. Decides between full sync (no cursor / expired) or delta sync.
 * 3. Applies chat-level changes (add, update, remove) to Dexie.
 * 4. Fetches new messages for changed chats (delta sync only).
 * 5. Fetches per-chat events (edits, deletes, reactions) and applies them
 *    (delta sync only — skipped on first sync and fullSync fallback).
 *    Events run AFTER new messages so they can target messages that
 *    arrived while offline.
 * 6. Handles thread comment-count updates.
 * 7. Persists both cursors only on complete success.
 * 8. Bounds concurrency for parallel per-chat fetches.
 */

import Dexie from "dexie";
import { db } from "@/indexedDB/db";
import type { ChatEntity, MessageEntity } from "@/indexedDB/types";
import { mapApiChatToEntity } from "@/indexedDB/services/syncMappers";
import { cascadeDeleteMessageDependents } from "@/indexedDB/services/syncCascadeDelete";
import { seedAvatarMapFromSyncedChats } from "@/indexedDB/services/syncAvatarSeed";
import { useAuthState } from "@/zustand/AuthState";
import { getMutedChats } from "@/server/services/chatService";
import {
  getLatestMessagesByChatIdFromLocalDB,
  hasUnsentLocalMessages,
} from "@/indexedDB/services/messages";
import { navigateToChatList } from "@/lib/navigationRef";
import { ChatType } from "@/server/types/chat";
import { createLogger } from "@/utils/debug";
import { useChatListState } from "@/zustand/ChatListState";
import { useNavigationState } from "@/zustand/NavigationState";
import {
  fetchChatList,
  fetchDeltaSync,
  fetchEvents,
  fetchNewMessages,
  type ChatListResponse,
  type EventsResponse,
  type SyncEvent,
  type UpdatedThread,
} from "./syncApiClient";
// Per-chat event-cursor map + numeric (ms,seq) cursor utils — PR-A1 of the
// sync-event-cursor-deadlock plan, used only under `featureFlags.syncEventDrainV2`.
import {
  EventCursorStore,
  bucketEventCursorAge,
  clampEventCursorToWindow,
  compareEventCursors,
  isStreamEventCursor,
  isoFloorOfEventCursor,
  parseEventCursor,
} from "./syncEventCursors";
// Static import is safe: syncScheduler pulls only axios + the logger, NOT crypto
// (the reason the crypto-bearing modules below are lazy-imported).
import { SyncSchedulerTimeout, getSchedulerSnapshot } from "./syncScheduler";
import { generateUuid7 } from "@/utils/uuid7";
// Telemetry is dependency-light (queue + config + localStorage) and carries NO
// PII — safe to import statically. It is fire-and-forget and never throws.
import { reportSyncTelemetry } from "./syncTelemetry";
import { featureFlags } from "@/config/featureFlags";
// Dependency-light (pure Dexie reads + zustand getState) — safe to import
// statically. Used only when `syncWindowedHydration` is on (WS2 Phase 2a).
import { computeHydrationWindow } from "./syncWindow";

const log = createLogger("SyncEngine");

// Lazy import to avoid pulling auth/crypto/zustand into the sync engine's module
// graph. `windowIds` (WS2 Phase 2a) prioritises the visible window's previews.
const lazyDecryptPreviews = (windowIds?: string[]) =>
  import("./syncDecryptionPass").then((m) =>
    m.decryptLastMessagePreviews(windowIds),
  );

// Lazy import (same reason as above) for the decryption-retry orchestrator.
// After delta events are applied, edited messages are flagged `failed` and (when
// backfilled) carry their ciphertext; this pass decrypts them in place from the
// v14 sidecar so the row heals right after sync. P3-c: pass the chats CHANGED
// this run so the pass scopes to them instead of a global all-chats failed-row
// scan on every sync.
const lazyRunDecryptionRetry = (chatIds: string[]) =>
  import("@/middleware/decryptionRetryOrchestrator").then((m) =>
    m.runDecryptionRetryPass(chatIds),
  );

// Lazy import (same reason as above — keeps crypto out of the sync engine's
// static module graph) for inline-decrypting backfilled messages. Rows whose
// eCSK we hold are decrypted in place from the ciphertext the page already
// returned, so the orchestrator no longer refetches each message individually.
const lazyDecryptBackfillRows = () =>
  import("./syncBackfillDecrypt").then((m) => m.decryptBackfillRows);

// Lazy import (same reason — keeps crypto out of the static module graph) for
// the G1 lazy-decrypt pass: re-decrypts ALL pending ciphertext rows from their
// STORED bytes (never a network refetch) on the new-sync coarse trigger. It is
// itself gated by `featureFlags.useNewSync` and is a no-op when off.
const lazyRunLazyDecryptPending = () =>
  import("./syncLazyDecryptPending").then((m) => m.runLazyDecryptPendingPass());

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum number of concurrent per-chat event fetches. */
const MAX_CONCURRENCY = 5;

/**
 * Sampling rate for the routine `sync_run_summary` telemetry event. Anomaly /
 * timeout events are NEVER sampled (we need numerator truth); only the
 * happy-path denominator counter is sampled so it can't dominate the per-client
 * daily cap. ~2%.
 */
const SYNC_RUN_SUMMARY_SAMPLE_RATE = 0.02;

/** Max pagination iterations to prevent runaway loops on server bugs. */
const MAX_PAGINATION_PAGES = 100;

/**
 * Max chatIds per `/sync/events` POST. The BE contract already accepts up to
 * 100 (`events/api/v2/routes.ts` rejects >100), so we send one POST per 100
 * chats and let the server fan out — instead of slicing into groups of 5 and
 * issuing ceil(N/5) POSTs per page. This is the bulk of the /sync/events
 * request-count cut (P2-lite). Steady-state pacing is handled by the shared
 * sync scheduler (token bucket), so no separate inter-page delay is needed.
 */
const EVENT_CHATIDS_PER_REQUEST = 100;

/**
 * Bulk-removal sanity gate (Codex review).
 *
 * INVARIANT: `removedChatIds` is only trustworthy when the backend derived it
 * from a COMPLETE membership view. The BE currently computes it from an
 * UNBOUNDED `Member.find`, so it is correct today — but if that query ever
 * becomes partial (a `.limit()`, projection bug, replica lag, timeout, or a
 * refactor), the FE would mass-delete VALID local groups → silent, real data
 * loss (see the data-loss analysis: a removed chat also wipes any PENDING/
 * FAILED local-only messages that the server never received).
 *
 * Defense: if a single delta would remove an implausibly large number of chats,
 * we DON'T trust the delta. Instead we skip the per-id deletes and escalate to
 * a full reconciliation (`fullChatListSync`), which re-derives the local set
 * from a complete authoritative snapshot and prunes correctly. A full snapshot
 * cannot be "partial" in the same way — it pages every chat to completion — so
 * orphan removal off it is safe.
 *
 * Two complementary thresholds (either tripping escalates):
 * - Absolute: more than MAX_BULK_REMOVALS chats removed in one delta.
 * - Relative: removals exceed BULK_REMOVAL_FRACTION of the local non-P2P set
 *   (catches mid-size accounts where a smaller count is still implausible).
 *
 * The relative threshold has a floor (BULK_REMOVAL_MIN_FOR_FRACTION): on very
 * small accounts a legitimate departure (leave 1 of 2 groups) is 50% but is in
 * no way "implausible", and escalating to a full resync for it would be a
 * needless round-trip + false alarm. So the fraction check only applies once at
 * least a few chats are being removed; below that, only the absolute cap can
 * trip the gate (and a handful of removals never exceeds 50).
 *
 * All counts are measured AFTER the P2P guard — only group chats that actually
 * exist locally and would actually be deleted count toward the gate.
 */
const MAX_BULK_REMOVALS = 50;
const BULK_REMOVAL_FRACTION = 0.3;
const BULK_REMOVAL_MIN_FOR_FRACTION = 5;

/**
 * Max times a chat's offline-message fetch is retried across syncs before we
 * give up. The event cursor is held while a chat is still being retried (so its
 * edit/delete/reaction events re-apply once the fetch succeeds); without a cap a
 * permanently-failing chat would stall event-cursor advancement for the whole
 * account forever. After the cap we accept the localised event loss for that one
 * chat and let the cursor advance.
 */
const MAX_MESSAGE_FETCH_RETRIES = 5;

/**
 * Mirror of BE config.SYNC_EVENTS_TTL_DAYS (=14). The server retains sync events
 * for this window; a full-resync event catch-up never needs (and the server
 * can't serve) anything older, so we floor the replay `since` at now−TTL. SOFT
 * coupling: if the BE shrinks its TTL this stays safe (server returns only what
 * it retains); if the BE GROWS it, widen this too.
 */
const SYNC_EVENTS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * Max follow-up rounds for chats starved by the BE global limit in one
 * `/sync/events` chunk (v2 drain only). A follow-up batch of only-starved
 * chats usually makes progress (the competition that starved them has
 * drained), but it is NOT immune — one busy chat can re-starve the batch via
 * the BE global limit — so the rounds are bounded and stop early when a round
 * makes no progress. Remaining starved chats simply resume from their
 * per-chat cursors next run.
 */
const MAX_STARVED_REQUEUE_ROUNDS = 3;

/** Meta store keys — constants to prevent typos across call sites. */
export const META_SYNC_CURSOR = "syncCursor" as const;
export const META_EVENT_CURSOR = "lastEventCursor" as const;
export const META_FAILED_MESSAGE_CHATS = "failedMessageChats" as const;
export const META_FAILED_MESSAGE_ATTEMPTS = "failedMessageAttempts" as const;
// Set when a cold-start `reconcileMutedChats` swallows a getMutedChats failure
// (issue #936). Subsequent DELTA runs retry the reconcile until one succeeds, so
// a transient failure no longer strands mute until the next full resync. Cleared
// on a successful reconcile; wiped on logout with the rest of `meta` (clearLocalDB).
export const META_PENDING_MUTE_RECONCILE = "pendingMuteReconcile" as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Run an array of async tasks with bounded concurrency.
 */
async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
): Promise<T[]> {
  const results: T[] = [];
  let index = 0;

  async function runNext(): Promise<void> {
    while (index < tasks.length) {
      const currentIndex = index++;
      results[currentIndex] = await tasks[currentIndex]();
    }
  }

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () =>
    runNext(),
  );

  await Promise.all(workers);
  return results;
}

// ---------------------------------------------------------------------------
// Event application
// ---------------------------------------------------------------------------

/**
 * Find a message by its server ID and apply an update.
 * Per-event error isolation: failures are logged but do not halt sync.
 */
async function findAndUpdateMessage(
  messageId: string,
  updater: (existing: Record<string, unknown>) => Record<string, unknown>,
  strict = false,
): Promise<void> {
  try {
    const existing = await db.messages.where("id").equals(messageId).first();
    if (!existing) {
      return;
    }

    const updates = updater(existing as unknown as Record<string, unknown>);
    await db.messages.update(existing._clientId, updates);
  } catch (err) {
    // Per-event error isolation (HIGH 7): log and continue.
    // `strict` (P0-b): when applying inside the transactional event-apply path,
    // RE-THROW so the page's transaction aborts and the chat is held for re-apply
    // next sync (events are idempotent) instead of the cursor silently skipping
    // the failed event. Non-strict callers (e.g. advisory thread-count updates)
    // keep swallowing.
    // eslint-disable-next-line no-console
    console.warn(`[syncEngine] Failed to update message ${messageId}:`, err);
    if (strict) {
      throw err;
    }
  }
}

/**
 * Find a message by its server ID and delete the row from Dexie.
 * Per-event error isolation: failures are logged but do not halt sync.
 */
async function findAndDeleteMessage(
  messageId: string,
  strict = false,
): Promise<void> {
  let deleted: MessageEntity | undefined;
  try {
    const existing = await db.messages.where("id").equals(messageId).first();
    if (!existing) {
      return;
    }
    deleted = existing;
    await db.messages.delete(existing._clientId);
  } catch (err) {
    // Per-event error isolation (HIGH 7) — `strict` (P0-b) re-throws so the
    // transactional event-apply path holds the cursor (see findAndUpdateMessage).
    // eslint-disable-next-line no-console
    console.warn(`[syncEngine] Failed to delete message ${messageId}:`, err);
    if (strict) {
      throw err;
    }
    return;
  }

  // If the deleted message was the chat's last-message preview, recompute it
  // from the newest remaining local message (mirrors the WS DELETE path in
  // messageEventsHandler). The preview is a denormalized, advisory copy, so a
  // failure here is always best-effort — it must NOT hold the cursor, even in
  // strict mode (the source-of-truth delete above already succeeded).
  try {
    await refreshChatPreviewAfterDelete(deleted.chatId, messageId);
  } catch (err) {
    log.warn("[sync] refreshChatPreviewAfterDelete failed", err);
  }

  // Cascade the delete to the message's dependent rows (issue #978): pinned
  // entry, cached attachment rows (+ OPFS blobs), link-preview row, and the
  // parent thread's comment-count. The cascade writes to stores OUTSIDE this
  // path's ambient transaction scope (the strict event-apply path only opens
  // `[messages, chats]`), and it must NEVER roll back or block the
  // source-of-truth message delete above — so when we are inside a transaction
  // we defer it to run AFTER that transaction COMMITS (if the transaction
  // aborts, the message was not actually deleted and the cascade correctly does
  // not run). `cascadeDeleteMessageDependents` is itself best-effort (never
  // throws), so a detached failure only logs.
  await scheduleCascadeDelete(deleted);
}

/**
 * Run the message-dependents cascade AFTER the ambient Dexie transaction (if
 * any) commits. Outside a transaction (e.g. a direct/non-strict call) it runs
 * and is awaited inline. See `cascadeDeleteMessageDependents` for the rationale
 * on why the cascade cannot run inside the strict event-apply transaction.
 */
function scheduleCascadeDelete(deleted: MessageEntity): Promise<void> {
  const tx = Dexie.currentTransaction;
  if (tx) {
    // Fire only on successful commit; detached + best-effort by contract.
    // Returning immediately avoids awaiting post-commit work inside the tx.
    tx.on("complete", () => {
      void cascadeDeleteMessageDependents(deleted);
    });
    return Promise.resolve();
  }
  return cascadeDeleteMessageDependents(deleted);
}

// Exported for regression tests (data-loss-sensitive cascade — see #978).
export { findAndDeleteMessage };

/**
 * After a message is deleted, refresh the chat's last-message preview if the
 * removed message was the one being shown. Picks the newest remaining local
 * message (UUID7 id ordering) or clears the preview if none remain.
 */
async function refreshChatPreviewAfterDelete(
  chatId: string,
  deletedMessageId: string,
): Promise<void> {
  const chat = await db.chats.get(chatId);
  if (!chat) {
    return;
  }
  const wasPreview =
    chat.lastMessage?.id === deletedMessageId ||
    chat.lastMessageCiphertext?.id === deletedMessageId;
  if (!wasPreview) {
    return;
  }

  // Use the shared helper so the candidate is filtered to top-level
  // message/service rows (not thread comments) — matches the WS DELETE path.
  const latest = await getLatestMessagesByChatIdFromLocalDB(chatId, 1);
  const newest = latest[0];

  if (newest) {
    await db.chats.update(chatId, {
      lastMessage: newest,
      lastMessageTimestamp: newest.timestamp,
      // Clear any stale ciphertext so hydration does not re-create a preview
      // for the deleted message.
      lastMessageCiphertext: null,
    });
  } else {
    await db.chats.update(chatId, {
      lastMessage: null,
      lastMessageTimestamp: undefined,
      lastMessageCiphertext: null,
    });
  }
}

/**
 * After an offline edit is applied to a message row, refresh the chat's
 * denormalized last-message preview if the edited message IS that preview.
 *
 * The chat list reads `chat.lastMessage` (a denormalized copy), NOT the live
 * `messages` row, so flagging the row `failed` in `applyEvent` does not by
 * itself update the preview — it would keep showing the stale pre-edit text.
 * Mark the preview copy `failed` too (the list parser then shows "Decrypting…"
 * instead of wrong text) and clear its decrypted text; the post-sync preview
 * hydration / decryption-retry pass re-decrypts the edited ciphertext.
 */
async function refreshChatPreviewAfterEdit(messageId: string): Promise<void> {
  // Updates the denormalized preview; the local try/catch logs and continues.
  // NOTE: when called from the strict transactional event-apply path (P0-b),
  // this runs INSIDE the per-chat `db.transaction`, so a Dexie write error here
  // still aborts that transaction regardless of the catch (Dexie does not
  // preventDefault just because the promise was caught). That outcome is SAFE
  // and intentional: the chat is then held and re-applies next sync (events are
  // idempotent) — it just means a preview-write failure rolls back the
  // (already-idempotent) edit flag rather than being silently swallowed.
  try {
    const existing = await db.messages.where("id").equals(messageId).first();
    if (!existing) {
      return;
    }
    const chat = await db.chats.get(existing.chatId);
    if (!chat?.lastMessage || chat.lastMessage.id !== messageId) {
      return;
    }
    await db.chats.update(existing.chatId, {
      lastMessage: {
        ...chat.lastMessage,
        content: { text: "" },
        isEdited: true,
        decryptionStatus: "failed",
        decryptionAttempts: 0,
      },
    });
  } catch (err) {
    log.warn("[sync] refreshChatPreviewAfterEdit failed", err);
  }
}

async function applyEvent(event: SyncEvent, strict = false): Promise<void> {
  switch (event.type) {
    case "message.edited":
      if (event.data.content != null) {
        // `event.data.content` is the EDITED message's ciphertext, not
        // plaintext — storing it directly would replace the decrypted
        // `{ text, ... }` content object with an unreadable string. Instead,
        // flag the row `failed` (attempts reset to 0) AND retain the edited
        // ciphertext on the v14 sidecar so the decryption-retry orchestrator
        // decrypts it from LOCAL bytes (P3-RESIDUAL) — no per-edit getMessage.
        //
        // Also CLEAR the stale decrypted content to the failed-row invariant
        // (`content: { text: "" }`, the same shape syncNewMessagesForChats uses
        // for not-yet-decrypted rows). Renderers like MessageBubbleContent read
        // `message.content.text` directly without checking decryptionStatus, so
        // leaving the OLD plaintext would keep displaying the pre-edit text
        // until retry heals the row. Showing empty (pending re-decrypt) is
        // strictly better than showing wrong/old text.
        await findAndUpdateMessage(
          event.messageId,
          () => ({
            isEdited: true,
            content: { text: "" },
            decryptionStatus: "failed",
            decryptionAttempts: 0,
            ciphertext: {
              content: event.data.content as string,
              attachmentMeta:
                (event.data.attachmentMeta as string | null | undefined) ??
                null,
            },
          }),
          strict,
        );
        // Keep the chat-list preview consistent if the edited message is the
        // denormalized last-message preview (it lives on the chat row, not the
        // messages table). Best-effort — never let it break the sync run.
        await refreshChatPreviewAfterEdit(event.messageId);
      }
      break;

    case "message.metaUpdate":
      // Metadata-only update (e.g. chunked-upload status "uploading"→"complete"
      // /"failed"): the text content is UNCHANGED, so — unlike message.edited —
      // do NOT clear `content` or flip `isEdited`. Store the refreshed ciphertext
      // (new attachment_meta carries the new uploadState) and re-queue decryption
      // so the lazy pass refreshes `content.media.uploadState` from local bytes.
      // The existing decrypted text stays visible throughout (it's correct).
      if (event.data.content != null) {
        await findAndUpdateMessage(
          event.messageId,
          () => ({
            decryptionStatus: "failed",
            decryptionAttempts: 0,
            ciphertext: {
              content: event.data.content as string,
              attachmentMeta:
                (event.data.attachmentMeta as string | null | undefined) ??
                null,
            },
          }),
          strict,
        );
      }
      break;

    case "message.deleted":
      await findAndDeleteMessage(event.messageId, strict);
      break;

    case "reaction.added":
    case "reaction.removed":
      // Replace the whole reactions map with the server's post-change summary,
      // mirroring the WS path (reactionEventsHandler sets `reactions: summary`).
      // The normalizer forwards the full summary in `event.data.summary`, so we
      // don't patch a single emoji key — that would leave stale emojis and a
      // phantom `{ [emoji]: undefined }` entry on a full removal.
      await findAndUpdateMessage(
        event.messageId,
        () => ({
          reactions: (event.data.summary as Record<string, number>) ?? {},
        }),
        strict,
      );
      break;

    default:
      // Unknown event type — skip silently
      break;
  }
}

async function applyThreadUpdates(
  updatedThreads: UpdatedThread[],
): Promise<void> {
  for (const thread of updatedThreads) {
    // The BE returns the parent/thread message id under `parentId` (see
    // eventsHandler — `{ chatId, parentId: threadId, commentCount }`), which is
    // the id of the local message row that owns the thread. Guard against a
    // missing/empty id so we never call `findAndUpdateMessage(undefined)`
    // (Dexie throws "Invalid key" and aborts the whole sync).
    if (!thread.parentId) {
      continue;
    }
    await findAndUpdateMessage(thread.parentId, () => ({
      commentCount: thread.commentCount,
    }));
  }
}

// ---------------------------------------------------------------------------
// Chat entity mapping
// ---------------------------------------------------------------------------

// `mapApiChatToEntity` lives in the dependency-light `./syncMappers` module so
// consumers (e.g. the websocket message handler) can normalize a server chat
// without importing the whole sync-engine graph. It is imported at the top for
// internal use and re-exported here for existing call sites.
export { mapApiChatToEntity };

// ---------------------------------------------------------------------------
// Merge-style upsert
// ---------------------------------------------------------------------------

/**
 * Upsert incoming chat entities into Dexie, preserving local-only fields
 * (firstPhaseMsgListInit, fullMsgListInit, lastMessage, etc.) for existing rows.
 *
 * New chats receive safe defaults. Existing chats have server sync fields
 * merged in while local-only fields are re-asserted so they cannot be
 * clobbered by a sync cycle.
 */
export async function upsertSyncedChats(incoming: ChatEntity[]): Promise<void> {
  if (incoming.length === 0) {
    return;
  }

  await db.transaction("rw", db.chats, async () => {
    const ids = incoming.map((c) => c.id);
    const existingRows = await db.chats.bulkGet(ids);
    const existingMap = new Map<string, ChatEntity>();
    for (const row of existingRows) {
      if (row != null) {
        existingMap.set(row.id, row);
      }
    }

    const merged: ChatEntity[] = incoming.map((chat) => {
      const existing = existingMap.get(chat.id);
      if (!existing) {
        // New chat — init local-only fields
        return {
          ...chat,
          firstPhaseMsgListInit: false,
          fullMsgListInit: false,
        };
      }
      // Existing chat — server wins for sync fields, local-only fields preserved
      return {
        ...existing,
        ...chat,
        firstPhaseMsgListInit: existing.firstPhaseMsgListInit,
        fullMsgListInit: existing.fullMsgListInit,
        firstMessageId: existing.firstMessageId,
        batchCount: existing.batchCount,
        renderedBatchCount: existing.renderedBatchCount,
        // Mute is treated as a LOCAL-ONLY field here: the sync chat-list DTO
        // (/umbra/v2/chats) does NOT carry `isMuted` — it lives in a separate
        // per-user endpoint (getMutedChats). So `chat.isMuted` from
        // mapApiChatToEntity is effectively always `false` today (the mapper does
        // `src.isMuted || false`, and the field is absent on the wire); without
        // this re-assertion a delta `updatedChats` upsert would clobber a muted
        // chat back to unmuted on every sync cycle. The only writers of mute are
        // the live toggle (updateChatInLocalDB) and the cold-start mute reconcile
        // (reconcileMutedChats); sync preserves it. (If the DTO ever starts
        // carrying authoritative mute, revisit this preservation.)
        isMuted: existing.isMuted,
        lastMessage:
          chat.lastMessage ??
          (existing.lastMessage?.decryptionStatus === "ok"
            ? existing.lastMessage
            : null) ??
          null,
        lastMessageCiphertext:
          chat.lastMessageCiphertext ?? existing.lastMessageCiphertext ?? null,
        lastMessageTimestamp:
          chat.lastMessageTimestamp ?? existing.lastMessageTimestamp,
      };
    });

    await db.chats.bulkPut(merged);
  });
}

// ---------------------------------------------------------------------------
// Mute reconciliation (cold-start parity)
// ---------------------------------------------------------------------------

/**
 * Restore per-chat mute state after a COMPLETE full chat-list snapshot.
 *
 * The new-sync chat-list DTO (`/umbra/v2/chats`) does NOT carry `isMuted` — mute
 * lives in a separate per-user endpoint (`getMutedChats`). So a cold-start full
 * sync rebuilds every chat with `isMuted=false` (logout calls `clearLocalDB`,
 * wiping the previously-restored flag). The legacy path
 * (`middleware/chatsSync.ts`) avoids this by fetching `getMutedChats` in parallel
 * and applying it; the new sync engine never did. This is the same class of
 * cold-start state-parity gap as pinned-message reconciliation.
 *
 * This helper fetches the muted-id set once and SET-reconciles `isMuted` for
 * EVERY local chat in a single transaction (`isMuted = mutedSet.has(id)`) — not
 * append-only, so a chat unmuted on another device is correctly cleared, matching
 * legacy `updateLocalDatabase`'s `mutedChatIds.includes(chat.id)` semantics.
 *
 * Must run ONLY after a complete snapshot, and AFTER `upsertSyncedChats` has
 * written the rows (freshly-inserted rows have no prior `isMuted` for the merge
 * to preserve). It is `await`ed inside `runSync` (never fire-and-forget) so a
 * prior session's muted-ids can't bleed onto a new user's rows after
 * `clearLocalDB`. A `getMutedChats` failure is logged and swallowed: mute is
 * non-critical relative to the chat list, and aborting the whole sync over it
 * would be worse than failing the whole sync. Instead of stranding mute until the
 * next full resync (issue #936), a swallowed failure records the
 * `pendingMuteReconcile` meta flag; subsequent DELTA runs retry the reconcile
 * (`maybeRetryPendingMuteReconcile`) until one succeeds. A genuine success clears
 * the flag; a user-swap abort leaves it untouched (the correct user's next run
 * heals it). Flag ownership lives ENTIRELY here so callers (first-sync,
 * recoverViaFullResync, the delta retry) all get identical self-healing semantics.
 */
export async function reconcileMutedChats(): Promise<void> {
  const username = useAuthState.getState().username;
  if (!username) {
    // Not logged in — not a failure, and not something a retry would fix. Leave
    // the flag exactly as-is (do not set or clear it).
    log.warn("reconcileMutedChats: no username in AuthState — skipping");
    return;
  }

  // Set the retry flag, but ONLY if the active user hasn't swapped during the
  // (awaited) getMutedChats call. `clearLocalDB` on logout wipes `meta` fire-and-
  // forget and is NOT serialized by the sync lock, so writing the flag after a
  // logout/login would resurrect a stale flag into the new user's freshly-cleared
  // meta (benign — one extra harmless getMutedChats next run — but the success
  // path already fences this, so keep the failure path consistent). (Claude review.)
  const setPendingFlagIfStillCurrentUser = async (): Promise<void> => {
    if (useAuthState.getState().username !== username) {
      log.warn(
        "reconcileMutedChats: active user changed during getMutedChats — " +
          "skipping pendingMuteReconcile flag to avoid cross-session resurrection",
      );
      return;
    }
    await db.meta.put({ key: META_PENDING_MUTE_RECONCILE, value: true });
  };

  let mutedIds: unknown;
  try {
    mutedIds = await getMutedChats(username);
  } catch (err) {
    log.warn(
      "reconcileMutedChats: getMutedChats failed — scheduling a delta-run retry " +
        "(pendingMuteReconcile) so mute is not stranded until the next full resync " +
        "(non-critical, sync continues)",
      err,
    );
    await setPendingFlagIfStillCurrentUser();
    return;
  }

  // Defend the SET-reconcile against a non-array response. getMutedChats does no
  // payload validation (it returns `response.data` verbatim); a malformed 200
  // (e.g. an error object, or an auth/edge response normalized to a non-array)
  // must NOT be treated as "no chats are muted" — that would mass-clear every
  // local mute. Only an actual array is authoritative; anything else: leave mute
  // as-is and bail. (A genuine empty array IS authoritative and correctly clears
  // stale local mutes — that case is intentionally allowed.) A non-array CAN be a
  // transient normalized auth/edge response (see comment above), so schedule a
  // retry too rather than permanently strand mute on it.
  if (!Array.isArray(mutedIds)) {
    log.warn(
      "reconcileMutedChats: getMutedChats returned a non-array payload — " +
        "leaving mute as-is and scheduling a delta-run retry (pendingMuteReconcile)",
    );
    await setPendingFlagIfStillCurrentUser();
    return;
  }

  const mutedSet = new Set<string>(mutedIds);

  // The txn distinguishes a genuine apply/no-op (flag should clear) from a
  // user-swap abort (flag must be left for the correct user's next run). A naive
  // post-txn `delete` would wrongly clear the retry on a swap abort.
  let abortedForUserSwap = false;
  let appliedCount = 0;
  await db.transaction("rw", db.chats, async () => {
    const all = await db.chats.toArray();

    // Session-fence (re-checked INSIDE the txn, AFTER the `await toArray()`). An
    // in-flight getMutedChats can outlive a logout/login that ran `clearLocalDB`
    // and swapped the user; the check must come AFTER the `await toArray()`
    // boundary so a user-swap during that await can't slip through (a check
    // placed before the await would leave that window open). It must ALSO come
    // before the `changed.length === 0` short-circuit below: otherwise a swap on
    // a clean no-op reconcile would go undetected and the post-txn flag-clear
    // would wrongly clear the retry for the new user (Codex review). If the
    // active user changed, abort so (a) a prior session's muted-ids can't be
    // written onto the new user's synced rows AND (b) the flag is left intact.
    if (useAuthState.getState().username !== username) {
      abortedForUserSwap = true;
      log.warn(
        "reconcileMutedChats: active user changed during getMutedChats — " +
          "aborting mute apply to avoid cross-session bleed",
      );
      return;
    }

    const changed = all.filter((c) => c.isMuted !== mutedSet.has(c.id));
    if (changed.length === 0) {
      return;
    }

    appliedCount = changed.length;
    // Write ONLY the `isMuted` field per chat (not a whole-row bulkPut of the
    // snapshot read above). A whole-row write would clobber any other field a
    // concurrent writer changed between this read and the write — most notably
    // a live mute toggle (updateChatInLocalDB), which runs in its own
    // transaction and does NOT hold the sync lock. Narrowing to the single
    // field keeps the blast radius to mute only.
    await Promise.all(
      changed.map((c) =>
        db.chats.update(c.id, { isMuted: mutedSet.has(c.id) }),
      ),
    );
  });

  // Clear the retry flag ONLY on a genuine reconcile (apply or clean no-op). A
  // user-swap abort did not reconcile this user's mute, so leave the flag for the
  // correct user's next delta run. (delete is a no-op when the key is absent.)
  //
  // Re-check the current user one more time: the in-txn fence only covers swaps
  // DURING the chats transaction, but a logout/login can also land in the window
  // between the txn committing and this delete. Clearing then would wipe the NEW
  // session's legitimately-set pending flag (its own first-sync may have failed),
  // re-stranding mute — exactly the bug this fix closes. (Codex PR review.) The
  // delete itself is independent of the chats txn, so a non-fenced delete here is
  // not protected by `abortedForUserSwap`.
  if (!abortedForUserSwap && useAuthState.getState().username === username) {
    await db.meta.delete(META_PENDING_MUTE_RECONCILE);
  }

  log.info(
    `reconcileMutedChats: updated ${appliedCount} chat row(s) ` +
      `(${mutedSet.size} muted on server)`,
  );
}

/**
 * Delta-run self-heal for issue #936. If a prior cold-start/full-resync
 * `reconcileMutedChats` swallowed a `getMutedChats` failure (recorded as the
 * `pendingMuteReconcile` meta flag), retry the reconcile here at the top of a
 * delta run. `reconcileMutedChats` clears the flag on success and re-sets it on a
 * repeat failure, so this naturally retries every delta run until one succeeds —
 * without holding or failing the sync cursor over a non-critical field.
 *
 * No-op (and no network call) when the flag is unset — the steady-state cost on a
 * healthy delta run is a single already-batched `meta` read.
 */
export async function maybeRetryPendingMuteReconcile(
  pendingFlag?: boolean,
): Promise<void> {
  const pending =
    pendingFlag ??
    (await db.meta.get(META_PENDING_MUTE_RECONCILE))?.value === true;
  if (!pending) {
    return;
  }
  log.info(
    "maybeRetryPendingMuteReconcile: pendingMuteReconcile set — retrying mute reconcile",
  );
  await reconcileMutedChats();
}

// ---------------------------------------------------------------------------
// Core sync
// ---------------------------------------------------------------------------

/**
 * Paginate one chat-list collection, writing each page to Dexie as it
 * arrives (bounded memory).
 *
 * `type` selects the collection: the default (group) list, or the P2P list
 * (`'p2p'`) which lives in a separate collection with no Member rows. The
 * group call passes no `type` so it remains a bare `fetchChatList(cursor)`.
 */
async function paginateChatList(
  syncRunId: string,
  type?: "p2p",
): Promise<{ chatIds: string[]; complete: boolean }> {
  const chatIds: string[] = [];
  let cursor: string | null | undefined;
  let hasMore = true;
  let pages = 0;
  // Why the traversal ended incomplete, if it did — reported (NO PII) so we can
  // tell a server cursor bug ("no-cursor") from genuinely-huge accounts ("cap").
  let incompleteReason: "cap" | "no-cursor" | "threw" | undefined;

  while (hasMore && pages < MAX_PAGINATION_PAGES) {
    let page: ChatListResponse;
    try {
      page = type
        ? await fetchChatList(cursor ?? undefined, undefined, type)
        : await fetchChatList(cursor ?? undefined);
    } catch (err) {
      // A scheduler rate-limit GIVE-UP (SyncSchedulerTimeout) is account-wide,
      // transient, and run-fatal: re-throw so the whole run aborts BEFORE any
      // cursor is persisted (cursors HOLD, the next trigger retries the full
      // fetch). Swallowing it here would risk seeding a fresh cursor off a
      // throttled-short snapshot and stranding the unreached tail. Mirrors the
      // message-fetch path's SyncSchedulerTimeout handling. Emit the
      // incomplete-list anomaly first (NO PII) — a 429 give-up mid-list is the
      // starvation signal this telemetry exists to size — then re-throw.
      if (err instanceof SyncSchedulerTimeout) {
        incompleteReason = "threw";
        reportSyncTelemetry("chat_list_incomplete", syncRunId, {
          type: type ?? "group",
          pagesFetched: pages,
          reason: incompleteReason,
        });
        throw err;
      }
      // Any OTHER mid-traversal throw (a non-429 transient/network error, a
      // 4xx/5xx, a parse failure): do NOT let it abort the WHOLE sync run by
      // exception. Stop the loop and report the snapshot INCOMPLETE — exactly
      // like the page-cap / no-cursor cases below. Pages already upserted this
      // run are kept; `complete: false` guarantees callers won't prune local
      // chats off this partial traversal (see recoverViaFullResync), and the
      // sibling (p2p) list still gets a turn.
      // NOTE: a sustained-429 give-up does NOT reach here — the scheduler
      // surfaces it as SyncSchedulerTimeout, handled by the re-throw above
      // (run-fatal, cursors hold, retried next trigger). This branch is the
      // safety net for every OTHER mid-traversal failure so one bad page can't
      // strand the tail (AI/hubId + older chats, which sort later) by exception.
      log.warn(
        "paginateChatList: fetchChatList threw mid-traversal — stopping " +
          "pagination and marking snapshot incomplete (already-fetched pages " +
          "are kept; no prune off a partial snapshot).",
        { type: type ?? "group", pagesFetched: pages, err },
      );
      incompleteReason = "threw";
      break;
    }
    // Write each page to Dexie immediately to bound memory
    const mapped = page.chats.map(mapApiChatToEntity);
    if (mapped.length > 0) {
      await upsertSyncedChats(mapped);
      // GAP-2: seed the persisted UserAvatarMapState from this full-snapshot
      // page so a stale persisted map entry can't override the peer's current
      // avatar (useUserAvatar reads the map before chat.avatar.attachmentId).
      // Full-snapshot ONLY — this is the full-fetch path; delta seeding is
      // deliberately omitted (real-time avatar changes flow through the
      // websocket user-event path). Seeds NON-EMPTY p2p peer avatars only, so
      // it is a no-op until ow2-chat-be PR #210 ships. See syncAvatarSeed.
      seedAvatarMapFromSyncedChats(mapped);
    }
    chatIds.push(...mapped.map((c) => c.id));
    cursor = page.cursor;
    hasMore = page.hasMore;
    pages++;
    // Defensive: if the server reports more pages but gives no cursor to
    // advance with, the next iteration would just re-request page 1 (cursor
    // undefined) up to the page cap — redundant requests + repeated upserts.
    // Stop; `complete: !hasMore` below already marks the snapshot incomplete so
    // callers won't prune local chats off a partial traversal.
    if (hasMore && cursor == null) {
      log.warn(
        "paginateChatList: hasMore=true with no cursor — stopping pagination and marking snapshot incomplete",
      );
      incompleteReason = "no-cursor";
      break;
    }
  }

  // The mid-traversal non-429 throw (caught + `break`ed above with
  // `incompleteReason = "threw"`) is a partial snapshot too — emit its anomaly
  // here (the run is NOT aborted, so it falls through to the `hasMore` block).
  if (incompleteReason === "threw") {
    reportSyncTelemetry("chat_list_incomplete", syncRunId, {
      type: type ?? "group",
      pagesFetched: pages,
      reason: incompleteReason,
    });
  } else if (hasMore) {
    // Bailed on the page cap with the server still reporting more pages (unless
    // it was the no-cursor break above).
    incompleteReason ??= "cap";
    reportSyncTelemetry("chat_list_incomplete", syncRunId, {
      type: type ?? "group",
      pagesFetched: pages,
      reason: incompleteReason,
    });
  }

  // `complete` is false whenever the traversal is PARTIAL: we bailed on the page
  // cap with the server still reporting more pages, hit a `hasMore`-with-no-cursor
  // page, OR a `fetchChatList` call threw mid-traversal (e.g. a transient/429
  // error short of a run-fatal scheduler give-up). In every case `hasMore` is
  // still true at the break, so `!hasMore` is false. Callers that prune local
  // chats off this snapshot must not do so when incomplete (a partial snapshot
  // would orphan valid chats — see recoverViaFullResync).
  return { chatIds, complete: !hasMore };
}

/**
 * Perform a full chat list fetch, handling pagination.
 *
 * The group (default) list and the P2P list live in separate server
 * collections — P2P chats have no Member row, so the group endpoint never
 * returns them and they must be requested explicitly via `type='p2p'`. Both
 * lists are paginated (each bounded by MAX_PAGINATION_PAGES).
 *
 * Returns all fetched chat IDs plus `complete` — true only when BOTH lists
 * paginated to exhaustion (`hasMore=false`), false if either hit the page cap
 * with more pages remaining. Orphan-pruning is only safe when `complete`.
 */
async function fullChatListSync(syncRunId: string): Promise<{
  chatIds: string[];
  complete: boolean;
}> {
  const group = await paginateChatList(syncRunId);
  const p2p = await paginateChatList(syncRunId, "p2p");
  return {
    chatIds: [...group.chatIds, ...p2p.chatIds],
    complete: group.complete && p2p.complete,
  };
}

/**
 * Thrown when a full chat-list snapshot could not be completed (pagination hit
 * the page cap, or a page reported `hasMore` with no cursor to advance with).
 *
 * It is a SYNC FAILURE on purpose: callers must NOT persist a fresh stream
 * cursor off an incomplete snapshot. Seeding a `Date.now()` cursor would switch
 * the next run to delta mode "from now" and permanently strand the chats that
 * were never reached. Throwing aborts before any cursor is persisted, so the
 * next sync retries the full fetch instead of locking in a partial dataset.
 */
export class IncompleteFullSyncError extends Error {
  constructor(fetchedCount: number) {
    super(
      `Full chat-list snapshot incomplete (fetched ${fetchedCount} chats before ` +
        `the pagination cap / a missing cursor). Aborting without persisting a ` +
        `cursor so the next sync retries the full fetch.`,
    );
    this.name = "IncompleteFullSyncError";
  }
}

/**
 * Authoritative recovery: re-fetch the chat list and prune any local chats that
 * no longer exist on the server.
 *
 * Shared by every full-reconciliation path — the cursor-expired fullSync
 * fallback, the bulk-removal sanity-gate escalation, and the null-cursor
 * anomaly fallback. Each of those then seeds a fresh stream cursor from "now"
 * and skips events (we just re-fetched everything), so this helper deliberately
 * owns only the resync + orphan prune; the caller owns cursor seeding.
 *
 * Orphan removal is gated on a COMPLETE snapshot. A full snapshot can't be
 * "partial" the way a delta's removedChatIds can — UNLESS pagination bailed on
 * MAX_PAGINATION_PAGES with the server still reporting more pages. In that case
 * the chat rows are still upserted (page-by-page), but we SKIP the prune: an
 * incomplete snapshot would mass-delete valid local groups (and their PENDING/
 * FAILED local-only messages) that simply weren't reached. The orphans get
 * pruned on a later complete sync instead.
 */
async function recoverViaFullResync(
  localChatIds: string[],
  syncRunId: string,
  reason: "fullsync-fallback" | "bulk-removal-gate" | "null-cursor-anomaly",
): Promise<void> {
  // full_resync_started — fires at every recoverViaFullResync trigger. Bucket
  // the local set size so we can size how often (and on what account sizes) the
  // cold-start / fallback resync runs. NO PII (count is bucketed downstream).
  reportSyncTelemetry("full_resync_started", syncRunId, {
    reason,
    localChatCount: localChatIds.length,
  });
  const result = await fullChatListSync(syncRunId);
  if (!result.complete) {
    // full_resync_incomplete — the resync could not page to completion (cap or a
    // no-cursor page). This is an anomaly event (never sampled away): it tells us
    // the recovery itself is being starved on heavy accounts.
    reportSyncTelemetry("full_resync_incomplete", syncRunId, {
      reason,
      localChatCount: localChatIds.length,
      fetchedCount: result.chatIds.length,
    });
    // Incomplete snapshot: skip the orphan prune (a partial snapshot would
    // mass-delete valid local chats that weren't reached) AND abort the whole
    // sync. Returning here would let the caller seed a fresh `Date.now()`
    // cursor and switch to delta mode, permanently stranding the unreached
    // chats. Throwing aborts before any cursor is persisted, so the next run
    // retries the full resync. (Copilot review.)
    log.warn(
      "Full-resync snapshot incomplete (hit the pagination cap / a page with " +
        "no cursor) — aborting sync without pruning or advancing the cursor so " +
        "the next run retries the full fetch.",
      { fetchedCount: result.chatIds.length, localCount: localChatIds.length },
    );
    throw new IncompleteFullSyncError(result.chatIds.length);
  }
  const serverChatIds = new Set(result.chatIds);
  const orphanedIds = localChatIds.filter((id) => !serverChatIds.has(id));
  // Prune orphans, but SKIP any that still hold unsent local-only messages (the
  // reconciliation prune guard — see removeChatAndMessages). A skipped chat keeps
  // its row so the next complete resync re-evaluates it once it either drains its
  // unsent queue or is confirmed truly gone.
  let prunedCount = 0;
  let skippedUnsentCount = 0;
  for (const id of orphanedIds) {
    const removed = await removeChatAndMessages(id, { guardUnsent: true });
    if (removed) {
      prunedCount += 1;
    } else {
      skippedUnsentCount += 1;
    }
  }
  if (skippedUnsentCount > 0) {
    // Near-miss data-loss anomaly: a chat absent from the authoritative server
    // snapshot still held unsent local-only messages, so its destructive prune
    // was skipped. COUNTS ONLY — never chat ids / usernames / content.
    reportSyncTelemetry("prune_skipped_unsent", syncRunId, {
      reason,
      path: "full-resync",
      skippedCount: skippedUnsentCount,
      orphanCount: orphanedIds.length,
    });
  }
  // full_resync_completed — the resync paged to completion and pruned orphans.
  // `removedCount` is how many local chats were ACTUALLY pruned (a count, not
  // ids); `skippedUnsentCount` is how many orphans were spared by the unsent
  // guard.
  reportSyncTelemetry("full_resync_completed", syncRunId, {
    reason,
    localChatCount: localChatIds.length,
    fetchedCount: result.chatIds.length,
    removedCount: prunedCount,
    skippedUnsentCount,
  });

  // Restore per-chat mute state on this complete snapshot. The sync chat-list
  // DTO doesn't carry `isMuted`, so the upserted rows above are all unmuted until
  // this set-reconcile applies getMutedChats. Runs after the prune so it only
  // touches surviving rows. (Same cold-start parity gap as pinned messages.)
  // This is the single drain point for ALL full-resync entry paths (cursor-
  // expired, bulk-removal gate, null-cursor anomaly), so each restores mute.
  await reconcileMutedChats();
}

/**
 * After an authoritative full chat-list resync, plan a BOUNDED event catch-up so
 * message deletes/edits/reactions that streamed during the resync window are not
 * lost (the resync replaces chat ENTITIES + prunes orphan CHATS, but never touches
 * message rows in surviving chats). Returns:
 *  - chatIds: chats that have LOCAL message rows AND still exist on the server. A
 *    delete only matters where a row exists (findAndDeleteMessage early-returns
 *    otherwise, syncEngine.ts:234), so this is correctness-equivalent to "all chats"
 *    for deletes while staying bounded — critical because too_many_chats fires on
 *    200+-chat accounts and an all-chats replay would re-create the request storm.
 *  - sinceFloor: now−TTL, the oldest `since` the replay may use.
 * MUST be called only AFTER recoverViaFullResync returned (it throws on an incomplete
 * snapshot, so reaching here guarantees db.chats is the complete authoritative set).
 */
async function planResyncEventCatchUp(): Promise<{
  chatIds: string[];
  sinceFloor: string;
}> {
  const sinceFloor = new Date(Date.now() - SYNC_EVENTS_TTL_MS).toISOString();
  // Read both stores in ONE read-txn so a concurrent WS chat add/remove can't make
  // the server-set and the local-message-set inconsistent (low-impact, but cheap to
  // close).
  const { serverIds, localMsgChatIds } = await db.transaction(
    "r",
    [db.chats, db.messages],
    async () => ({
      serverIds: new Set((await db.chats.toArray()).map((c) => c.id)),
      localMsgChatIds: (await db.messages
        .orderBy("chatId")
        .uniqueKeys()) as string[],
    }),
  );
  return {
    chatIds: localMsgChatIds.filter((id) => serverIds.has(id)),
    sinceFloor,
  };
}

/**
 * Pick the `since` for a full-resync event catch-up. Trust the persisted event
 * cursor ONLY if it is a sane, recent value — i.e. within `[floor, now]`. Anything
 * else — older than the TTL floor, MISSING, or in the FUTURE (mobile clock skew /
 * corrupt meta) — falls back to `floor` so we always replay the retained delete
 * window rather than skipping it. A future cursor is the dangerous case: without
 * the upper bound, `since` would be future and the catch-up would skip every
 * retained delete, silently re-introducing the bug.
 *
 * Lexical string comparison == chronological here because both bounds and the
 * persisted `META_EVENT_CURSOR` are always fixed-width `Date.toISOString()` output
 * (the event cursor is only ever set to `syncStartTime` or carried forward from a
 * prior ISO value; the Redis stream id `<ms>-<seq>` lives only in `META_SYNC_CURSOR`).
 * If a non-ISO value is ever written to `META_EVENT_CURSOR`, switch to Date-parse compare.
 */
function clampReplaySince(
  held: string | undefined,
  floor: string,
  nowIso: string,
): string {
  return held && held >= floor && held <= nowIso ? held : floor;
}

/**
 * Apply one `/sync/events` page: per-chat transactional event apply (P0-b)
 * followed by best-effort thread comment-count updates. Shared by the v1
 * drain (`syncEventsForChats`) and the v2 drain (`drainEventChunkV2`) —
 * factored out unchanged, so v1 behavior is identical to the previous inline
 * block.
 *
 * A failure applying any event for a chat aborts that chat's transaction (so
 * the page does not partially apply) and reports the chat in
 * `failedApplyChatIds` — the caller marks it incomplete / holds its cursor so
 * the page re-applies next sync (events are idempotent) instead of the cursor
 * silently skipping the failed event. `applyEvent(.., true)` re-throws on a
 * source-of-truth write failure to trip the abort; advisory denormalized
 * previews stay best-effort inside it. Decrypt is N/A here — applyEvent only
 * flags edited rows, so the whole apply is Dexie-only and safe to wrap in a
 * transaction.
 *
 * Thread comment-count updates are advisory/denormalized and are kept OUT of
 * the per-chat transactions; a failure there must not hold any cursor.
 */
async function applyEventsPage(result: EventsResponse): Promise<{
  failedApplyChatIds: Set<string>;
  appliedChatIds: string[];
}> {
  const failedApplyChatIds = new Set<string>();
  // Chats with ≥1 event SUCCESSFULLY applied on this page (P3-c scope input).
  const appliedChatIds: string[] = [];
  for (const [chatId, chatEvents] of Object.entries(result.eventsByChat)) {
    try {
      await db.transaction("rw", [db.messages, db.chats], async () => {
        for (const event of chatEvents.events) {
          await applyEvent(event, true);
        }
      });
      if (chatEvents.events.length > 0) {
        appliedChatIds.push(chatId);
      }
    } catch (err) {
      log.warn("[sync] event page apply failed — holding cursor for chat", {
        chatId,
        err,
      });
      failedApplyChatIds.add(chatId);
    }
  }

  await applyThreadUpdates(result.updatedThreads);

  return { failedApplyChatIds, appliedChatIds };
}

/**
 * Fetch and apply events for a set of chat IDs, with pagination.
 *
 * Tracks per-chat cursors so completed chats are not re-fetched (CRITICAL 1),
 * and captures `until` once before the loop to prevent drift (MEDIUM 10).
 */
async function syncEventsForChats(
  chatIds: string[],
  since: string,
): Promise<{ incomplete: string[]; withEvents: string[] }> {
  if (chatIds.length === 0) {
    return { incomplete: [], withEvents: [] };
  }

  // Capture `until` once before the pagination loop (MEDIUM 10)
  const until = new Date().toISOString();

  // Per-chat cursor tracking (CRITICAL 1)
  const chatCursors = new Map<string, string>();
  for (const id of chatIds) {
    chatCursors.set(id, since);
  }
  let pendingChatIds = [...chatIds];
  let iterations = 0;
  // Chats whose events could NOT be fully drained this run — either a page
  // reported `hasMore` without a cursor to advance with, or the page-cap was
  // hit while still pending. Returned so the caller can HOLD the event cursor
  // and let these re-apply next sync (events are idempotent) rather than
  // advancing past undelivered events and losing them. (Codex review.)
  const incomplete = new Set<string>();
  // Chats that had ≥1 event SUCCESSFULLY applied this run (P3-c). `applyEvent`'s
  // edit branch marks rows `failed`, so these are exactly the chats the scoped
  // post-event decryption-retry pass must heal.
  const withEvents = new Set<string>();

  while (pendingChatIds.length > 0 && iterations < MAX_PAGINATION_PAGES) {
    iterations++;
    // Build per-chat since values — use each chat's own cursor
    const currentSinceValues = pendingChatIds.map(
      (id) => chatCursors.get(id) ?? since,
    );
    // Use the earliest cursor as `since` for the batch request
    const batchSince = currentSinceValues.reduce((a, b) => (a < b ? a : b));

    const result = await fetchEvents(pendingChatIds, batchSince, until);

    // Apply this page (per-chat transactional apply + advisory thread counts —
    // see applyEventsPage). A chat whose apply failed is marked incomplete so
    // its cursor HOLDS and the page re-applies next sync.
    const { failedApplyChatIds, appliedChatIds } =
      await applyEventsPage(result);
    for (const chatId of appliedChatIds) {
      withEvents.add(chatId);
    }
    for (const chatId of failedApplyChatIds) {
      incomplete.add(chatId);
    }

    // Update per-chat cursors and filter completed chats (CRITICAL 1)
    const nextPendingChatIds: string[] = [];
    for (const chatId of pendingChatIds) {
      // A chat whose apply failed this page is already marked incomplete; stop
      // paginating it this run so it re-drains from the held cursor next sync.
      if (failedApplyChatIds.has(chatId)) {
        continue;
      }
      const chatEvents = result.eventsByChat[chatId];
      if (chatEvents?.hasMore) {
        if (chatEvents.cursor) {
          // More to fetch and we have a cursor to make progress — keep going.
          chatCursors.set(chatId, chatEvents.cursor);
          nextPendingChatIds.push(chatId);
        } else {
          // `hasMore` but no cursor: re-requesting with the same `since` would
          // not advance (and could spin until the page-cap). Mark incomplete
          // and stop re-requesting this chat so it can't starve the budget.
          incomplete.add(chatId);
        }
      }
      // If no hasMore or no entry, this chat is done — don't re-request
    }

    pendingChatIds = nextPendingChatIds;
  }

  // Chats still pending after the page-cap were not fully drained either.
  for (const id of pendingChatIds) {
    incomplete.add(id);
  }
  if (incomplete.size > 0) {
    log.warn(
      "Event pagination did not fully drain some chats — holding the event " +
        "cursor so undelivered events re-apply next sync (events are idempotent).",
      { incompleteCount: incomplete.size, pageCap: MAX_PAGINATION_PAGES },
    );
  }
  return { incomplete: [...incomplete], withEvents: [...withEvents] };
}

// ---------------------------------------------------------------------------
// Event drain v2 — per-chat cursors + checkpointing (flag `syncEventDrainV2`)
// PR-A1 of .plans/in-progress/sync-event-cursor-deadlock/plan.md
// ---------------------------------------------------------------------------

/**
 * Visibility probe for the v2 drain's run boundary. SSR/vitest-safe. NOTE:
 * this is the STOP side only — the resume TRIGGER on `visible` already lives
 * in useSync's visibilitychange listener and is untouched.
 */
function isDocumentHidden(): boolean {
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
}

/**
 * V2 event drain: resolves each chat's effective `since` through the fallback
 * chain (per-chat entry → legacy `META_EVENT_CURSOR` → TTL floor, numerically
 * clamped to `[floor, now]`), groups chats by cursor proximity so laggards
 * batch with laggards (a laggard co-batched with current chats would drag the
 * batch `since` back and re-download their windows), drains each 100-chunk
 * with mid-run checkpointing, and derives the legacy cursor to keep writing
 * for rollback builds.
 *
 * Structural fix for the deadlock: progress is now PER CHAT and PER PAGE
 * (durable via `EventCursorStore.checkpoint`), so an interrupted run — the
 * field-dominant failure (96% of scheduler_timeouts are background-throttling
 * interruptions) — RESUMES instead of replaying the whole window, and one
 * straggler chat holds only itself, not the account-wide cursor.
 *
 * The legacy `META_EVENT_CURSOR` keeps being written (≥1 release) as the
 * ISO-normalized numeric MIN over this run's chats' end positions, so a
 * rollback build resumes sanely (its `clampReplaySince` reads fixed-width
 * ISO). It is DERIVED-ONLY here: never read back over a per-chat entry
 * (mixed-version tabs can advance it beyond laggard positions).
 */
async function runEventDrainV2(
  eventChatIds: string[],
  legacyEventCursor: string | undefined,
  nowIso: string,
  syncRunId: string,
  retryableFailedChatIds: string[],
): Promise<{
  incomplete: string[];
  withEvents: string[];
  derivedLegacyCursor: string;
  /** Telemetry (§4) — attached to the SAMPLED sync_run_summary. NO PII. */
  oldestDrainAgeBucket: string | undefined;
  laggardCount: number;
  maxLaggardAgeBucket: string | undefined;
}> {
  const store = await EventCursorStore.load();
  const floorIso = new Date(Date.now() - SYNC_EVENTS_TTL_MS).toISOString();
  // Chats whose NEW-MESSAGE backfill failed this run (still under the retry
  // cap). v1 held the GLOBAL event cursor for these so their edit/delete/
  // reaction events re-apply next run once the messages finally exist
  // locally (applyEvent no-ops on a missing row). v2 keeps that invariant
  // PER CHAT: a held chat's events are still fetched and applied (idempotent,
  // and rows that DO exist heal now) but its per-chat cursor never advances.
  const heldChatIds = new Set(retryableFailedChatIds);

  // Effective per-chat since (lazy migration seed: a chat with no per-chat
  // entry seeds from max(legacy, floor) via the clamp — persisted only when
  // the chat actually advances through the guarded write site).
  const sinceByChat = new Map<string, string>();
  let migrationClampedCount = 0;
  for (const chatId of eventChatIds) {
    const persisted = store.get(chatId);
    const clamp = clampEventCursorToWindow(
      persisted ?? legacyEventCursor,
      floorIso,
      nowIso,
    );
    if (persisted !== undefined && clamp.reason !== null) {
      // Heal an out-of-window PERSISTED entry to the clamped value. A future
      // (clock-skew / corrupt) entry would otherwise defeat the monotonic
      // advance forever — every real candidate compares below it — so the
      // chat would replay its whole window every run; a below-floor entry is
      // unfetchable anyway. Persisted at the next checkpoint.
      store.repair(chatId, clamp.value);
    }
    if (
      persisted === undefined &&
      legacyEventCursor !== undefined &&
      clamp.reason === "below-floor"
    ) {
      // The chat's owed events (legacy cursor → floor) already expired
      // server-side before this client ever ran v2 — count the gap instead of
      // silently absorbing it in BE retention (plan R4).
      migrationClampedCount++;
    }
    sinceByChat.set(chatId, clamp.value);
  }
  if (migrationClampedCount > 0) {
    reportSyncTelemetry("event_gap_expired_at_migration", syncRunId, {
      chatCount: migrationClampedCount,
    });
  }

  // Persist any repair() heals NOW (no-op when nothing was repaired). A heal
  // must not depend on the first page's checkpoint happening — a hidden stop
  // or a fetch failure before that checkpoint would lose it and the corrupt
  // entry would re-trigger a full-window replay next run. (Review round 2.)
  await store.checkpoint();

  // checkpoint_resume (§4.3, anomaly-class, UNSAMPLED): the previous run was
  // interrupted mid-pagination iff a chat's persisted cursor is still a
  // stream-format checkpoint at run start — this run RESUMES it instead of
  // replaying the window. Counted AFTER the seed/heal pass and SCOPED to this
  // run's chats so the count is "checkpoints actually resumed" (impl-review
  // HIGH, both models): an out-of-window checkpoint was just healed to the
  // floor — that is a replay, not a resume — and a stale entry for a chat no
  // longer local resumes nothing (unhealed + unpruned until A2, it would
  // otherwise fire this anomaly EVERY run forever). Counts only, no ids.
  let resumedCheckpoints = 0;
  for (const chatId of eventChatIds) {
    const persisted = store.get(chatId);
    if (persisted !== undefined && isStreamEventCursor(persisted)) {
      resumedCheckpoints++;
    }
  }
  if (resumedCheckpoints > 0) {
    reportSyncTelemetry("checkpoint_resume", syncRunId, { resumedCheckpoints });
  }

  // Cursor-proximity grouping: sort by position BEFORE slicing into
  // 100-chunks. Stable sort keeps local order within equal positions.
  const sortedChatIds = [...eventChatIds].sort((a, b) =>
    compareEventCursors(
      sinceByChat.get(a) as string,
      sinceByChat.get(b) as string,
    ),
  );

  const incomplete = new Set<string>();
  const withEvents = new Set<string>();
  // Chats whose event apply FAILED this run (held at their position, no
  // advance — plan R2). Feeds the §4.2 laggard_count metric alongside the
  // stream-format checkpoints in the store.
  const applyFailedChatIds = new Set<string>();
  let stoppedHidden = false;

  for (let i = 0; i < sortedChatIds.length; i += EVENT_CHATIDS_PER_REQUEST) {
    if (stoppedHidden || isDocumentHidden()) {
      // Visibility-aware run boundary: no NEW pages once hidden. Progress up
      // to here is already checkpointed; the un-drained remainder holds the
      // run-level gates and resumes from its per-chat cursors next run.
      stoppedHidden = true;
      for (const chatId of sortedChatIds.slice(i)) {
        incomplete.add(chatId);
      }
      break;
    }
    const chunk = sortedChatIds.slice(i, i + EVENT_CHATIDS_PER_REQUEST);
    const chunkResult = await drainEventChunkV2(
      chunk,
      sinceByChat,
      store,
      syncRunId,
      heldChatIds,
    );
    for (const chatId of chunkResult.incomplete) {
      incomplete.add(chatId);
    }
    for (const chatId of chunkResult.withEvents) {
      withEvents.add(chatId);
    }
    for (const chatId of chunkResult.applyFailed) {
      applyFailedChatIds.add(chatId);
    }
    if (chunkResult.stoppedHidden) {
      stoppedHidden = true;
    }
  }

  // Derive the legacy cursor: numeric min over THIS RUN's chats' end
  // positions (persisted entry, else the effective since it ran with). Stale
  // map entries for chats no longer local are deliberately excluded so they
  // can't drag the min back forever (map pruning itself is deferred to A2).
  // CAVEAT: this is only rollback-safe while `eventChatIds` covers every
  // local chat with events owed (true today — the delta path passes ALL
  // local chats, and the full-resync catch-up scopes to chats that have
  // local message rows). If the drain scope ever narrows (plan Q1), a local
  // chat excluded from the min could be skipped by a ROLLBACK build.
  let minPosition: string | undefined;
  for (const chatId of eventChatIds) {
    const position = store.get(chatId) ?? (sinceByChat.get(chatId) as string);
    if (
      minPosition === undefined ||
      compareEventCursors(position, minPosition) < 0
    ) {
      minPosition = position;
    }
  }

  // --- Run-end telemetry (§4.1 + §4.2), all buckets/counts — NO PII. --------
  // Both metrics are SCOPED to this run's chats — the SAME scoping decision as
  // `derivedLegacyCursor` above, for the same reason (impl-review HIGH, both
  // models): stale map entries for chats no longer local are never healed or
  // pruned (pruning deferred to A2), so a whole-map read would saturate
  // oldestDrainAgeBucket at >=14d forever and permanently inflate laggards.
  const nowMs = Date.now();

  // (§4.1) oldest_cursor_age: age of the OLDEST end position across this
  // run's chats (the "is any individual chat still pinned" signal the
  // aggregate run duration hides). `minPosition` is already that numeric min;
  // in-scope garbage entries were healed to the floor by the read clamp.
  const oldestDrainAgeBucket =
    minPosition === undefined
      ? undefined
      : bucketEventCursorAge(nowMs - parseEventCursor(minPosition).ms);

  // (§4.2) laggard_count = this run's chats left at a STREAM-format checkpoint
  // (mid-pagination) ∪ chats HELD by an apply failure this run. maxLaggardAge
  // = the oldest such chat's age, bucketed. Positions: the persisted cursor,
  // else (apply-failed, never advanced) the effective since it ran with.
  let laggardCount = 0;
  let maxLaggardAgeMs = -1;
  for (const chatId of eventChatIds) {
    const persisted = store.get(chatId);
    const isLaggard =
      (persisted !== undefined && isStreamEventCursor(persisted)) ||
      applyFailedChatIds.has(chatId);
    if (!isLaggard) {
      continue;
    }
    laggardCount++;
    const position = persisted ?? (sinceByChat.get(chatId) as string);
    const ageMs = nowMs - parseEventCursor(position).ms;
    if (ageMs > maxLaggardAgeMs) {
      maxLaggardAgeMs = ageMs;
    }
  }
  const maxLaggardAgeBucket =
    laggardCount > 0 ? bucketEventCursorAge(maxLaggardAgeMs) : undefined;

  return {
    incomplete: [...incomplete],
    withEvents: [...withEvents],
    derivedLegacyCursor: isoFloorOfEventCursor(minPosition ?? nowIso),
    oldestDrainAgeBucket,
    laggardCount,
    maxLaggardAgeBucket,
  };
}

/**
 * Drain one ≤100-chat chunk under the v2 rules:
 *  - batch `since` = NUMERIC (ms,seq) min over the pending chats' positions
 *    (Bug #2 fix: the lexical string min inverts on variable-width stream
 *    seqs and silently skips events).
 *  - per-chat cursor advances go ONLY through the store's guarded monotonic
 *    write site; a fully-drained chat advances to the chunk's captured
 *    `until` (never "now at persist time").
 *  - one checkpoint per page, AFTER that page's apply transactions.
 *  - chats starved by the BE global limit (`hasMore` with NO cursor) are
 *    re-queued into follow-up batches of only-starved chats — bounded by
 *    MAX_STARVED_REQUEUE_ROUNDS + a no-progress stop (`starved_requeue`
 *    telemetry, counts only).
 *  - a failed apply holds THAT chat (no advance) — plan R2.
 *  - a HELD chat (new-message backfill failed under the retry cap —
 *    `heldChatIds`) still fetches/applies its events (idempotent) but NEVER
 *    persists an advance, preserving v1's "events re-apply after the backfill
 *    finally succeeds" guarantee per chat.
 *  - on visibilitychange→hidden the in-flight page finishes (+checkpoints)
 *    and no new page is requested.
 */
async function drainEventChunkV2(
  chunkChatIds: string[],
  sinceByChat: Map<string, string>,
  store: EventCursorStore,
  syncRunId: string,
  heldChatIds: ReadonlySet<string>,
): Promise<{
  incomplete: string[];
  withEvents: string[];
  stoppedHidden: boolean;
  /** Chats whose event apply FAILED this run (held, no advance) — §4.2. */
  applyFailed: string[];
}> {
  // Captured once per chunk; a drained chat advances to exactly this value —
  // a later "now" would skip the until→now gap (state-machine rule).
  const until = new Date().toISOString();

  // In-memory positions mirror the store's GUARDED values, so a regressed BE
  // page cursor can never drag the batch `since` back either.
  const positions = new Map<string, string>();
  for (const chatId of chunkChatIds) {
    positions.set(chatId, sinceByChat.get(chatId) as string);
  }

  const incomplete = new Set<string>();
  const withEvents = new Set<string>();
  // Apply-failure holds (R2) — reported to the caller for §4.2 laggard_count.
  const applyFailed = new Set<string>();
  // Page budget shared across the initial pass AND the starved re-queue
  // rounds, so the re-queue cannot exceed the chunk's page cap.
  let iterations = 0;
  let stoppedHidden = false;

  const drainPending = async (
    initialPending: string[],
  ): Promise<{ starved: string[]; progressed: boolean }> => {
    let pending = [...initialPending];
    const starved = new Set<string>();
    let progressed = false;

    while (pending.length > 0 && iterations < MAX_PAGINATION_PAGES) {
      if (isDocumentHidden()) {
        stoppedHidden = true;
        break;
      }
      iterations++;

      // NUMERIC batch since (Bug #2 fix).
      let batchSince = positions.get(pending[0]) as string;
      for (const chatId of pending) {
        const position = positions.get(chatId) as string;
        if (compareEventCursors(position, batchSince) < 0) {
          batchSince = position;
        }
      }

      const result = await fetchEvents(pending, batchSince, until);
      const { failedApplyChatIds, appliedChatIds } =
        await applyEventsPage(result);
      for (const chatId of appliedChatIds) {
        withEvents.add(chatId);
      }

      const nextPending: string[] = [];
      for (const chatId of pending) {
        if (failedApplyChatIds.has(chatId)) {
          // R2: the failed chat holds — no advance, no further pages this run.
          incomplete.add(chatId);
          applyFailed.add(chatId);
          continue;
        }
        const chatEvents = result.eventsByChat[chatId];
        if (chatEvents?.hasMore) {
          if (chatEvents.cursor) {
            if (heldChatIds.has(chatId)) {
              // HELD (its new-message backfill failed under the retry cap):
              // paginate IN MEMORY only — never persist — so the whole window
              // re-applies next run once the backfill succeeds (applyEvent
              // no-ops on rows that don't exist locally yet).
              const current = positions.get(chatId) as string;
              if (compareEventCursors(chatEvents.cursor, current) > 0) {
                positions.set(chatId, chatEvents.cursor);
              }
            } else {
              if (store.advance(chatId, chatEvents.cursor)) {
                progressed = true;
              }
              // Mirror the GUARDED value (monotonic), not the raw page cursor.
              positions.set(
                chatId,
                store.get(chatId) ?? (positions.get(chatId) as string),
              );
            }
            nextPending.push(chatId);
          } else {
            // Starved by the BE global limit: hasMore with no cursor means 0
            // events were returned for it — re-requesting inside THIS batch
            // cannot advance it, so hand it to the re-queue.
            starved.add(chatId);
          }
        } else if (heldChatIds.has(chatId)) {
          // Drained, but HELD: do NOT advance to `until` — the events applied
          // this run may have no-opped against still-missing message rows.
          // The chat re-drains (idempotent) from its held cursor next run.
          incomplete.add(chatId);
        } else {
          // Fully drained → advance to the chunk's captured `until`.
          store.advance(chatId, until);
          positions.set(chatId, store.get(chatId) ?? until);
          progressed = true;
        }
      }

      // Mid-run checkpoint — AFTER this page's apply transactions committed
      // (a crash in between replays idempotent events, never loses them).
      // Runs inside the sync lock (runSync executes under withSyncLock).
      await store.checkpoint();
      pending = nextPending;
    }

    // Still pending at the page cap / hidden stop — not drained this run.
    for (const chatId of pending) {
      incomplete.add(chatId);
    }
    return { starved: [...starved], progressed };
  };

  const firstPass = await drainPending(chunkChatIds);
  let starved = firstPass.starved;
  const starvedInitialCount = starved.length;
  let rounds = 0;
  while (
    starved.length > 0 &&
    rounds < MAX_STARVED_REQUEUE_ROUNDS &&
    !stoppedHidden &&
    iterations < MAX_PAGINATION_PAGES
  ) {
    rounds++;
    const round = await drainPending(starved);
    starved = round.starved;
    if (!round.progressed) {
      // No-progress stop: the follow-up batch starved again wholesale —
      // more rounds would just repeat the identical request.
      break;
    }
  }
  if (starvedInitialCount > 0) {
    // Counts only — NO chat ids (PII rule).
    reportSyncTelemetry("starved_requeue", syncRunId, {
      rounds,
      starvedCount: starvedInitialCount,
      remainingCount: starved.length,
    });
  }
  for (const chatId of starved) {
    incomplete.add(chatId);
  }

  return {
    incomplete: [...incomplete],
    withEvents: [...withEvents],
    stoppedHidden,
    applyFailed: [...applyFailed],
  };
}

/**
 * Remove a chat and all of its associated rows from IndexedDB.
 *
 * Sweeps EVERY chat-scoped store (those keyed/indexed by `chatId` in the
 * Dexie schema): `messages`, `pinnedMessages`, `mediaAttachments`,
 * `fileAttachments`, `voiceAttachments` and `links`. Leaving any of these
 * behind accumulates orphaned rows that no chat references, bloating IDB
 * over time.
 *
 * Each per-store sweep is best-effort and isolated: a failure in one store
 * (e.g. a query error) must not prevent the others — or the critical chat-row
 * removal — from running. The `chats.delete` at the end is the critical op.
 */
async function removeChatAndMessages(
  chatId: string,
  options?: { guardUnsent?: boolean },
): Promise<boolean> {
  // RECONCILIATION PRUNE GUARD (data-loss). On the server-driven reconciliation
  // paths (full-resync orphan prune + delta removedChatIds), NEVER hard-delete a
  // chat that still holds unsent LOCAL-ONLY messages (sendingStatus PENDING /
  // FAILED). Those messages exist ONLY on this device — server-backed rows
  // re-sync, unsent ones are gone forever. A false-orphan (a BE partial-
  // membership view, an auth/filter regression, a premature hasMore:false) that
  // sails under the bulk-removal count floor would otherwise silently, and
  // unrecoverably, delete them. Skip the removal and leave the chat row intact so
  // the NEXT reconciliation re-evaluates it; the caller telemeters the skip. A
  // genuinely server-removed chat that happens to hold an unsent message simply
  // persists locally until the user sends/discards — non-destructive.
  //
  // Only the reconciliation callers pass `guardUnsent: true`. INTENTIONAL user
  // deletes (leaving / deleting a chat) go through a DIFFERENT path
  // (removeChatFromLocalDB) and never reach here, so they still purge unsent
  // messages exactly as the user asked.
  if (options?.guardUnsent && (await hasUnsentLocalMessages(chatId))) {
    return false;
  }

  // Navigate away if the user is viewing the chat being removed
  const activeChatId = useNavigationState.getState().activeChatId;
  if (activeChatId === chatId) {
    navigateToChatList();
  }

  // Delete the DECRYPTED OPFS blobs BEFORE the rows (parity with
  // chatTeardown.removeChatDataFromLocalDB — 2026-08-17 review §5): a bare
  // row sweep strands plaintext on disk with nothing left pointing at it,
  // bounded only by the ≥10-min orphan cleaner. The attachment tables'
  // primary key `id` IS the attachment id. Best-effort; lazy import keeps
  // the storage module out of the sync engine's static graph.
  try {
    const [mediaIds, fileIds, voiceIds] = await Promise.all([
      db.mediaAttachments.where("chatId").equals(chatId).primaryKeys(),
      db.fileAttachments.where("chatId").equals(chatId).primaryKeys(),
      db.voiceAttachments.where("chatId").equals(chatId).primaryKeys(),
    ]);
    const opfsIds = [...mediaIds, ...fileIds, ...voiceIds] as string[];
    if (opfsIds.length) {
      const { deleteAttachmentContent } = await import(
        "@/lib/storage/attachmentStorage"
      );
      await Promise.all(
        opfsIds.map((id) => deleteAttachmentContent(id).catch(() => {})),
      );
    }
  } catch {
    // Blob cleanup is best-effort — the row sweep below is the critical op.
  }

  // Sweep all chat-scoped stores (HIGH 6). Each is best-effort and independent
  // so one failing store doesn't orphan rows in (or block) the others.
  const chatScopedStores = [
    db.messages,
    db.pinnedMessages,
    db.mediaAttachments,
    db.fileAttachments,
    db.voiceAttachments,
    db.links,
  ];
  await Promise.all(
    chatScopedStores.map(async (store) => {
      try {
        await store.where("chatId").equals(chatId).delete();
      } catch {
        // Per-store cleanup is best-effort — the chat removal is the critical op
      }
    }),
  );

  await db.chats.delete(chatId);
  return true;
}

// ---------------------------------------------------------------------------
// New message fetch (Task 4.2 Step A)
// ---------------------------------------------------------------------------

/**
 * Fetch new messages for a set of changed chats.
 *
 * For each chat, looks up the last local message ID (UUID7-ordered) and
 * fetches messages after it. New messages are stored in Dexie via bulkPut.
 *
 * Bounded by MAX_CONCURRENCY to match the event-fetch pattern.
 */
async function syncNewMessagesForChats(chatIds: string[]): Promise<string[]> {
  if (chatIds.length === 0) {
    return [];
  }

  const failedChatIds: string[] = [];
  // Classify each backfilled message as outgoing by comparing its sender to the
  // current user (same rule as the WS handler / messages middleware). Read the
  // username straight from the lightweight AuthState store rather than the
  // side-effecting auth middleware. Without this, a user's own message that
  // arrives via offline backfill (e.g. sent from another device) would be
  // stored isOutgoing:false → wrong bubble alignment and "You:" preview.
  const currentUsername = useAuthState.getState().username || null;

  // \u00a73.5 observability: count messages decrypted in place vs. stored needing
  // decryption (ciphertext retained). Incremented synchronously inside the
  // per-chat tasks (safe in JS's single-threaded model) and logged at the end.
  let inlineDecrypted = 0;
  let decryptFallback = 0;

  // Resolve the (crypto-bearing) inline-decrypt module lazily AND only when a
  // page actually has messages — a run whose changed chats yield no new messages
  // never loads crypto/WASM. `import()` caches; this shared promise dedupes the
  // resolution across the concurrent per-chat tasks.
  let decryptorPromise: ReturnType<typeof lazyDecryptBackfillRows> | null =
    null;

  const tasks = chatIds.map((chatId) => async () => {
    try {
      // Load the chat row once per chat \u2014 it carries the eCSK the inline
      // decrypt needs (mirrors the live-socket handler). A missing chat/eCSK
      // simply routes rows to the ciphertext-retaining fallback.
      const chat = await db.chats.get(chatId);

      // Find the latest message for this chat in IndexedDB
      const lastMessage = await db.messages
        .where("[chatId+id]")
        .between([chatId, ""], [chatId, "\uffff"])
        .last();

      // Paginate: the messages endpoint enforces a page size and signals more
      // pages with a non-null `cursor` (BE returns cursor=null when done — the
      // "hasMore from cursor presence" convention). Advance `after` to the
      // newest message id of each page (UUID7 ids sort lexicographically, and
      // `after` takes a message id) and loop until the cursor is null. Without
      // this, offline backfill silently dropped every message past page 1.
      let after = lastMessage?.id;
      let pages = 0;
      while (pages < MAX_PAGINATION_PAGES) {
        pages += 1;
        const result = await fetchNewMessages(chatId, after);

        if (result.messages.length > 0) {
          // Decrypt the page IN PLACE from the ciphertext it already carries
          // (mirrors the live-socket handler), instead of storing every row as
          // `failed` and letting the orchestrator refetch each one via
          // GET /api/messages — the per-message request storm. Rows we can't
          // decrypt yet (eCSK not synced / decrypt throws) keep their ciphertext
          // on the v14 sidecar so they re-decrypt from LOCAL bytes later. The
          // builder preserves all display fields (#871 reply-quote/tags + the
          // thread-count badge) and keeps input order (bulkPut is keyed by id).
          const decryptBackfillRows = await (decryptorPromise ??=
            lazyDecryptBackfillRows());
          const { rows, inline, fallback } = await decryptBackfillRows(
            result.messages,
            chat,
            currentUsername,
          );
          inlineDecrypted += inline;
          decryptFallback += fallback;
          await db.messages.bulkPut(rows);
        }

        // Done when the server reports no more pages.
        if (result.cursor == null || result.messages.length === 0) {
          break;
        }
        // Advance to the newest id in this page; guard a non-advancing cursor
        // (server bug) so we can't spin to the page cap re-fetching the window.
        const nextAfter = result.messages.reduce(
          (max, m) => (m.id > max ? m.id : max),
          result.messages[0].id,
        );
        if (nextAfter === after) {
          break;
        }
        after = nextAfter;
      }
    } catch (err) {
      // A scheduler rate-limit give-up (SyncSchedulerTimeout) is account-wide
      // and TRANSIENT — not a per-chat failure. Re-throw so the whole run aborts
      // BEFORE the cursor-persist transaction → both cursors HOLD and the next
      // trigger resumes. Aging it through the per-chat MAX_MESSAGE_FETCH_RETRIES
      // cap (designed for ONE permanently-broken chat) would, under a sustained
      // 429 storm, drop the chat from retryableFailedChatIds after 5 triggers and
      // let the cursor gate advance past its un-backfilled messages = silent loss,
      // precisely under the load this whole fix exists to handle.
      if (err instanceof SyncSchedulerTimeout) {
        throw err;
      }
      // Genuine per-chat error (4xx contract bug, etc.): isolate, log, and track
      // for retry next sync (HIGH 2) — one bad chat must not fail the whole run.
      failedChatIds.push(chatId);
      // eslint-disable-next-line no-console
      console.warn(
        `[syncEngine] Failed to fetch new messages for chat ${chatId}:`,
        err,
      );
    }
  });

  await runWithConcurrency(tasks, MAX_CONCURRENCY);

  // §3.5 observability: prove the per-message refetch storm is gone — held-key
  // rows decrypt in place (inlineDecrypted) and only genuinely-undecryptable
  // rows fall back (decryptFallback, ciphertext retained, still no refetch here).
  log.info("[sync] backfill inline-decrypt", {
    chats: chatIds.length,
    inlineDecrypted,
    decryptFallback,
  });

  return failedChatIds;
}

/**
 * Run the full sync/reconciliation pipeline.
 *
 * Cursor domains (kept strictly separate):
 * - `syncCursor` — stream ID from delta toCursor. Used for `/sync/chats`.
 * - `lastEventCursor` — ISO timestamp. Used as `since` for `/sync/events`.
 *
 * - If no syncCursor exists: full chat list fetch, skip events (no local
 *   messages to apply them to), save both cursors.
 * - If syncCursor exists: delta sync, fetch events using `lastEventCursor`,
 *   update both cursors on success.
 * - If cursor expired (fullSync fallback): full re-fetch + orphan cleanup,
 *   skip events, save both cursors.
 * - Persists cursors only on complete success.
 *
 * Public wrapper: mints the per-run `syncRunId` and captures the start time,
 * delegates the pipeline to `runSyncImpl`, and — on the run-fatal
 * `SyncSchedulerTimeout` (the heavy-account starvation signal we most need to
 * size) — emits a `scheduler_timeout` telemetry event with the scheduler's
 * contention snapshot before re-throwing so the caller's run-fatal handling is
 * unchanged.
 */
export async function runSync(): Promise<void> {
  // One id per runSync invocation — threaded into every telemetry event so the
  // dedupe guard can collapse repeats within a single run (the loop defense) and
  // so events from one run correlate. Anonymous + per-run; carries no PII.
  const syncRunId = generateUuid7();
  // Wall-clock start, for the sync_run_summary duration.
  const runStartedAt = Date.now();
  try {
    await runSyncImpl(syncRunId, runStartedAt);
  } catch (err) {
    // A scheduler rate-limit give-up is the heavy-account starvation signal we
    // most need to size. Emit it with the scheduler's contention snapshot (NO
    // PII — just token/cooldown counters) at the point it aborts the run, then
    // re-throw so the caller's existing run-fatal handling is unchanged.
    if (err instanceof SyncSchedulerTimeout) {
      const snap = getSchedulerSnapshot();
      reportSyncTelemetry("scheduler_timeout", syncRunId, {
        elapsedMs: Date.now() - runStartedAt,
        tokensAvailable: snap.tokensAvailable,
        bucketCapacity: snap.bucketCapacity,
        cooldownRemainingMs: snap.cooldownRemainingMs,
        inCooldown: snap.inCooldown,
      });
    }
    throw err;
  }
}

async function runSyncImpl(
  syncRunId: string,
  runStartedAt: number,
): Promise<void> {
  // Capture once to avoid clock drift across the sync run
  const syncStartTime = new Date().toISOString();
  // Start-of-run millis, in Redis stream-ID form (`<ms>-0`), used to seed the
  // stream cursor on every full-sync path. Seeding from START (not end) of the
  // snapshot means the next delta sync re-fetches anything that streamed in
  // while the full snapshot was running, rather than skipping that window.
  // Re-delivery is harmless (sync upserts are idempotent).
  const syncStartCursor = `${Date.now()}-0`;

  // Values derived from the persisted cursors (read inside the gated block
  // below) but needed in steps 2-3 AFTER the gate is released — declared here so
  // they outlive the reconciliation `try`.
  let existingSyncCursor: string | undefined;
  let existingEventCursor: string | undefined;
  let previouslyFailedChatIds: string[] = [];
  let previousFailedAttempts: Record<string, number> = {};

  let newSyncCursor: string;
  let newEventCursor: string | undefined;
  let shouldFetchEvents = false;
  let shouldFetchNewMessages = false;
  let eventChatIds: string[] = [];
  let changedChatIds: string[] = [];
  // P0-b: the trusted-delta path captures its `toCursor` here (instead of
  // committing it to `newSyncCursor` immediately) so the chat/stream cursor can
  // be GATED on full drainage after steps 2-3 — mirroring the event cursor.
  // Null on the first-sync/full-resync paths, which write a complete snapshot
  // and have no partial-application gap to guard against.
  let deltaToCursor: string | null = null;
  // Chats whose event pages could not be fully drained this run. Declared at
  // run scope (P0-b) so the chat-cursor gate after the events block can read it.
  const incompleteEventChatIds: string[] = [];
  // Which high-level path this run took — for the sampled sync_run_summary.
  let syncPath: "first" | "delta" | "full-resync" = "delta";
  // Bucket the local set size once (delta path) for the summary; 0 on cold start.
  let summaryChatCount = 0;

  // Phase-1 cold-start fix: the chat-list reconciliation gate is held for this
  // ENTIRE run. The gate is NOT raised here — it is owned by the trigger site
  // (`useSync`'s `triggerSync`), which calls `beginChatListReconciliation()`
  // SYNCHRONOUSLY before `await withSyncLock(() => runSync())` and balances it in
  // a `finally`. Raising it at the trigger (rather than as the first statement of
  // `runSync`, which only runs AFTER several awaits inside `withSyncLock` →
  // `navigator.locks.request`) guarantees the gate is up before ANY async hop on
  // every runSync path — so the decryption-retry orchestrator, firing on the SAME
  // socket-connect event, cannot observe depth 0 and slip a legacy `getMessage`
  // through. While it's held, non-essential background consumers (the
  // orchestrator's legacy refetch) defer via `awaitChatListReconciliationIdle`,
  // so the chat-list traversal owns the shared rate budget and is NON-STARVABLE.
  // The reconciliation does NOT await any background consumer, so no deadlock.
  // (This is a bare block, not a `try`, because the gate's begin/end now lives at
  // the chokepoint; keeping the block scope avoids re-indenting steps 2-3.)
  {
    // 1. Read the persisted cursors and previously failed chat IDs
    const [
      syncCursorEntry,
      eventCursorEntry,
      failedChatsEntry,
      failedAttemptsEntry,
      pendingMuteEntry,
    ] = await Promise.all([
      db.meta.get(META_SYNC_CURSOR),
      db.meta.get(META_EVENT_CURSOR),
      db.meta.get(META_FAILED_MESSAGE_CHATS),
      db.meta.get(META_FAILED_MESSAGE_ATTEMPTS),
      db.meta.get(META_PENDING_MUTE_RECONCILE),
    ]);
    const pendingMuteReconcile = pendingMuteEntry?.value === true;
    existingSyncCursor = syncCursorEntry?.value as string | undefined;
    existingEventCursor = eventCursorEntry?.value as string | undefined;
    previouslyFailedChatIds =
      (failedChatsEntry?.value as string[] | undefined) ?? [];
    previousFailedAttempts =
      (failedAttemptsEntry?.value as Record<string, number> | undefined) ?? {};

    if (!existingSyncCursor) {
      syncPath = "first";
      // -- First-time sync --
      // full_resync_started (reason: first-sync) — the cold-start full fetch. No
      // local chats yet, so the bucket is 0-100; the point is the COUNT of how
      // often a cold start happens, which the cold-start fix must reduce.
      reportSyncTelemetry("full_resync_started", syncRunId, {
        reason: "first-sync",
        localChatCount: 0,
      });
      // fullChatListSync writes each page to Dexie internally (bounded memory)
      const firstSync = await fullChatListSync(syncRunId);
      // If the very first snapshot is incomplete, abort WITHOUT seeding a cursor:
      // persisting a `Date.now()` cursor would switch the next run to delta mode
      // and permanently strand the chats that weren't reached. Throwing leaves the
      // cursor unset so the next run retries the full fetch. (Copilot review.)
      if (!firstSync.complete) {
        log.warn(
          "First-time full sync incomplete (hit the pagination cap / a page with " +
            "no cursor) — aborting without seeding a cursor so the next run retries.",
          { fetchedCount: firstSync.chatIds.length },
        );
        reportSyncTelemetry("full_resync_incomplete", syncRunId, {
          reason: "first-sync",
          fetchedCount: firstSync.chatIds.length,
        });
        throw new IncompleteFullSyncError(firstSync.chatIds.length);
      }
      reportSyncTelemetry("full_resync_completed", syncRunId, {
        reason: "first-sync",
        fetchedCount: firstSync.chatIds.length,
        removedCount: 0,
      });
      // Restore per-chat mute state on this complete first snapshot. The sync
      // chat-list DTO doesn't carry `isMuted` (it lives in getMutedChats), so every
      // row was just written unmuted; without this, mute is lost on every cold
      // start (logout clears Dexie). Mirrors legacy chatsSync's getMutedChats merge.
      await reconcileMutedChats();
      // Bootstrap a valid Redis stream cursor so the next sync can use delta mode.
      // (getChatList returns a pagination cursor, not a stream cursor.)
      // Use current timestamp as stream cursor seed so the next sync can use
      // delta mode. (getChatList returns a pagination cursor, not a stream cursor.)
      newSyncCursor = syncStartCursor;
      // No local messages exist yet — skip events, set event cursor to now
      newEventCursor = syncStartTime;
    } else {
      // -- Delta sync --
      // Self-heal a stranded cold-start mute reconcile (#936) BEFORE fetchDeltaSync,
      // so it fires regardless of which delta sub-path is taken below — including the
      // fullSync-fallback / bulk-removal / null-cursor escalations to
      // recoverViaFullResync (which would otherwise skip a per-sub-branch retry).
      // Non-blocking: a repeat failure just re-sets the flag for the next run; the
      // sync cursor is never held or failed over this non-critical field.
      await maybeRetryPendingMuteReconcile(pendingMuteReconcile);

      const localChats = await db.chats.toArray();
      const localChatIds = localChats.map((c) => c.id);
      summaryChatCount = localChatIds.length;
      // Local chat id → type, used to guard against deleting P2P chats (below).
      const localChatTypes = new Map<string, number>(
        localChats.map((c) => [c.id, c.type]),
      );

      const delta = await fetchDeltaSync(existingSyncCursor, localChatIds);

      if (delta.fullSync) {
        syncPath = "full-resync";
        // Cursor expired — fall back to full fetch (writes to Dexie per page) and
        // prune chats that no longer exist on the server.
        await recoverViaFullResync(
          localChatIds,
          syncRunId,
          "fullsync-fallback",
        );

        // Use current timestamp as stream cursor seed so the next sync can use
        // delta mode. (getChatList returns a pagination cursor, not a stream cursor.)
        newSyncCursor = syncStartCursor; // authoritative snapshot taken → no re-escalation
        const catchUp = await planResyncEventCatchUp();
        eventChatIds = catchUp.chatIds;
        shouldFetchEvents = true;
        // DO NOT set changedChatIds / shouldFetchNewMessages here. New-message
        // backfill is a PER-CHAT fetchNewMessages paginator (syncNewMessagesForChats)
        // — on a 200+-chat account that is ~200×pages GETs = the request storm. It is
        // NOT needed for the delete bug: a delete acts on a message row that already
        // exists locally (findAndDeleteMessage). The parent never backfilled on full-
        // resync; we keep it that way. (Both plan-review models, HIGH.)
        //
        // Clamp the event-replay floor to the retained TTL window. The full-resync
        // signal reflects the STREAM cursor, not lastEventCursor — which may be
        // old/held or MISSING — so do NOT trust it to be recent. max(held cursor,
        // now−TTL): replays from the held cursor when recent, else from the TTL floor
        // (also fixes the missing-cursor case: floor, not "now"). Reassigning the
        // run-scoped `existingEventCursor` makes the existing events/drain machinery
        // do the rest. Do NOT set newEventCursor here (the drain gate holds-vs-advances).
        existingEventCursor = clampReplaySince(
          existingEventCursor,
          catchUp.sinceFloor,
          syncStartTime,
        );
      } else {
        // Map first to get the correct `id` field. The actual upsert is deferred
        // until AFTER the anomaly gates below (bulk-removal sanity gate +
        // null-cursor check): an anomalous delta is fully abandoned and recovered
        // from an authoritative full snapshot, so we must not write ANY of its
        // payload (updates included) — only commit these chat updates on the
        // trusted (non-anomalous) path.
        const mappedUpdatedChats = delta.updatedChats.map(mapApiChatToEntity);

        // Remove chats and their messages (HIGH 6 + MEDIUM 11).
        //
        // DATA-LOSS GUARD: never delete a P2P chat on the strength of
        // removedChatIds alone. P2P chats have no Member row, so the backend
        // already excludes them from removedChatIds — but if it ever regresses
        // and sends a P2P id here, deleting it locally would lose the entire
        // conversation. Skip (and warn) any removedId whose local type is P2P;
        // genuinely-left group chats are still removed.
        //
        // BULK-REMOVAL SANITY GATE (Codex review): before applying the deletes,
        // count the group chats that actually exist locally and would actually be
        // removed (i.e. after the P2P guard). If that count is implausibly large
        // — see MAX_BULK_REMOVALS / BULK_REMOVAL_FRACTION — the delta is likely
        // built from a partial/incomplete server membership view. Trusting it
        // would mass-delete valid local groups (and their PENDING/FAILED local-
        // only messages). So we bail to a full reconciliation instead of deleting.
        const removableGroupIds = delta.removedChatIds.filter(
          (id) =>
            localChatTypes.has(id) && localChatTypes.get(id) !== ChatType.P2P,
        );
        const removedCount = removableGroupIds.length;
        const localNonP2PCount = localChats.filter(
          (c) => c.type !== ChatType.P2P,
        ).length;
        const tripsAbsolute = removedCount > MAX_BULK_REMOVALS;
        const tripsFraction =
          removedCount >= BULK_REMOVAL_MIN_FOR_FRACTION &&
          localNonP2PCount > 0 &&
          removedCount / localNonP2PCount > BULK_REMOVAL_FRACTION;

        if (removedCount > 0 && (tripsAbsolute || tripsFraction)) {
          syncPath = "full-resync";
          // Gate tripped — do NOT apply the per-id deletes for this delta.
          log.warn(
            "Bulk-removal sanity gate tripped — skipping delta deletes and " +
              "escalating to full reconciliation (removedChatIds is only " +
              "trustworthy from a COMPLETE membership view; a delta removing an " +
              "implausible fraction likely means a partial/incomplete server " +
              "response, so re-derive from a full snapshot rather than mass-" +
              "deleting).",
            {
              removedCount,
              localNonP2PCount,
              maxBulkRemovals: MAX_BULK_REMOVALS,
              bulkRemovalFraction: BULK_REMOVAL_FRACTION,
              trippedBy: tripsAbsolute ? "absolute" : "fraction",
            },
          );

          // Authoritative recovery: re-fetch the complete chat list and prune
          // orphans off that snapshot (same path as the fullSync fallback).
          await recoverViaFullResync(
            localChatIds,
            syncRunId,
            "bulk-removal-gate",
          );

          // CURSOR SAFETY: we deliberately do NOT advance to `delta.toCursor`
          // here. We seed a fresh stream cursor from "now" (the same scheme used
          // by the first-sync and fullSync-fallback paths). The gated delta is
          // therefore safely abandoned, not committed. Instead of skipping events
          // (which dropped message deletes), do the bounded TTL-floored, locally-
          // scoped event catch-up (same as the fullSync-fallback path above).
          newSyncCursor = syncStartCursor; // authoritative snapshot taken → no re-escalation
          const catchUp = await planResyncEventCatchUp();
          eventChatIds = catchUp.chatIds;
          shouldFetchEvents = true;
          // No new-message backfill (request-storm guard); no newEventCursor (the
          // drain gate holds-vs-advances). See the fullSync-fallback site for the
          // full rationale. Clamp the replay `since` to a sane [floor, now] window.
          existingEventCursor = clampReplaySince(
            existingEventCursor,
            catchUp.sinceFloor,
            syncStartTime,
          );
        } else if (delta.toCursor == null) {
          syncPath = "full-resync";
          // If server returned null cursor with fullSync=false, treat the whole
          // delta as an anomaly: do NOT apply its destructive per-id removals
          // (those would wipe group chats AND their local-only PENDING/FAILED
          // messages that the server never received — unrecoverable). Detect the
          // anomaly BEFORE any deletes, then recover authoritatively from a full
          // snapshot, which prunes genuine orphans off a complete view anyway.
          // (Issue 3 — checked before removals, same data-loss reasoning as the
          // bulk-removal sanity gate.)
          // eslint-disable-next-line no-console
          console.warn(
            "[syncEngine] delta.toCursor is null with fullSync=false — skipping delta deletes and falling back to full sync",
          );
          await recoverViaFullResync(
            localChatIds,
            syncRunId,
            "null-cursor-anomaly",
          );

          // Use current timestamp as stream cursor seed so the next sync can use
          // delta mode. (getChatList returns a pagination cursor, not a stream cursor.)
          // Instead of skipping events (which dropped message deletes), do the
          // bounded TTL-floored, locally-scoped event catch-up (same as the other
          // full-resync paths above).
          newSyncCursor = syncStartCursor; // authoritative snapshot taken → no re-escalation
          const catchUp = await planResyncEventCatchUp();
          eventChatIds = catchUp.chatIds;
          shouldFetchEvents = true;
          // No new-message backfill (request-storm guard); no newEventCursor (the
          // drain gate holds-vs-advances). See the fullSync-fallback site for the
          // full rationale. Clamp the replay `since` to a sane [floor, now] window.
          existingEventCursor = clampReplaySince(
            existingEventCursor,
            catchUp.sinceFloor,
            syncStartTime,
          );
        } else {
          // Trusted delta (passed the bulk-removal gate, non-null cursor): commit
          // its chat updates now. Deferred from the top of the delta branch so an
          // anomalous delta writes nothing (see comment there).
          if (mappedUpdatedChats.length > 0) {
            await upsertSyncedChats(mappedUpdatedChats);
            // GAP-2: seed the persisted avatar map from the delta too. This is
            // the PRIMARY carrier for an avatar a peer changed while we were
            // offline — fetchEvents has no USER_UPDATE event, so the new avatar
            // arrives only via updatedChats[].avatar. Same non-empty-only +
            // priority guard as the full-snapshot seam (no-op pre-#210); the
            // dedup inside skips unchanged peers so frequent deltas stay cheap.
            seedAvatarMapFromSyncedChats(mappedUpdatedChats);
          }

          // Track which removals ACTUALLY happened. Two data-loss guards can
          // spare a chat from deletion here: the P2P guard (below) and the unsent
          // local-only message guard (inside removeChatAndMessages). A spared chat
          // must stay in `eventChatIds` so it keeps syncing its edit/delete/
          // reaction events instead of silently stalling while the cursor advances.
          const actuallyRemovedIds: string[] = [];
          let skippedUnsentDeltaCount = 0;
          if (delta.removedChatIds.length > 0) {
            for (const removedId of delta.removedChatIds) {
              if (localChatTypes.get(removedId) === ChatType.P2P) {
                log.warn(
                  `Refusing to locally delete P2P chat ${removedId} from removedChatIds (data-loss guard)`,
                );
                continue;
              }
              const removed = await removeChatAndMessages(removedId, {
                guardUnsent: true,
              });
              if (removed) {
                actuallyRemovedIds.push(removedId);
              } else {
                // Unsent-message guard spared this removedChatId — same near-miss
                // data-loss reasoning as the full-resync orphan prune. A small-
                // count false removal (BE partial view) sails under the bulk-
                // removal gate, so guard it here too.
                skippedUnsentDeltaCount += 1;
              }
            }
          }
          if (skippedUnsentDeltaCount > 0) {
            // COUNTS ONLY — never chat ids / usernames / content.
            reportSyncTelemetry("prune_skipped_unsent", syncRunId, {
              path: "delta-removed",
              skippedCount: skippedUnsentDeltaCount,
            });
          }

          // toCursor is non-null here (the null-cursor anomaly is handled by the
          // `else if` branch above, before any deletes). Build the accurate chatId
          // list: add new chats, drop removed ones.
          //
          // Use `actuallyRemovedIds` (the chats actually deleted above), NOT the
          // raw `delta.removedChatIds`. A P2P id — or a group chat spared by the
          // unsent-message guard — is deliberately KEPT, so it must stay in
          // `eventChatIds`; otherwise the kept chat would silently stop syncing its
          // edit/delete/reaction events while the cursor advanced past them.
          const removedSet = new Set(actuallyRemovedIds);
          const newChatIds = mappedUpdatedChats
            .map((c) => c.id)
            .filter((id) => !localChatIds.includes(id));
          eventChatIds = [...localChatIds, ...newChatIds].filter(
            (id) => !removedSet.has(id),
          );
          // Track changed chats for new message fetch (Task 4.2 Step A)
          changedChatIds = mappedUpdatedChats.map((c) => c.id);
          // P0-b: do NOT advance the chat/stream cursor yet. Capture the delta's
          // toCursor and HOLD the cursor at its current value; the gate after the
          // event-apply step advances it to `deltaToCursor` only if this run fully
          // drained (no retryable message-fetch failures, no incomplete event
          // pages). Otherwise it stays put and the next delta re-includes the
          // chats that didn't drain — instead of silently skipping past them.
          deltaToCursor = delta.toCursor;
          newSyncCursor = existingSyncCursor;
          shouldFetchEvents = true;
          shouldFetchNewMessages = true;
        }
      }
    }
  }

  // WS2 Phase 2a (flag `syncWindowedHydration`, default off): compute the visible
  // window from local Dexie ONCE per run (indexed reverse cursors — O(window)).
  // Used to (a) backfill the window's new messages first in step 2 and (b)
  // decrypt the window's previews BEFORE the event drain (the early kick below).
  // Empty on a true cold start (no local rows) — degrades to a no-op. Computed
  // AFTER the gated block so it reflects this run's chat upserts.
  // Defense-in-depth: window hydration only ever runs under the new sync engine.
  // It is already unreachable otherwise (runSync only executes under useNewSync),
  // but gate explicitly so the code matches the comment / flag intent.
  const windowedHydration =
    featureFlags.syncWindowedHydration && featureFlags.useNewSync;
  let windowIds: string[] = [];
  if (windowedHydration) {
    try {
      windowIds = await computeHydrationWindow();
    } catch (err) {
      // Never let window computation break a run — fall back to global order.
      log.warn(
        "[sync] window compute failed; falling back to global order",
        err,
      );
      windowIds = [];
    }
  }
  const windowSet = windowIds.length > 0 ? new Set(windowIds) : null;

  // 2. Fetch new messages for changed chats (delta sync only — Task 4.2 Step A).
  // Must run BEFORE events so that edits/deletes/reactions can target messages
  // that arrived while offline (they need to exist in IDB first).
  // Skipped on first sync (no local messages yet) and fullSync fallback
  // (just re-fetched everything).
  // Merge previously failed chat IDs so they get retried (HIGH 2).
  let failedMessageChatIds: string[] = [];
  // The chats we fetched new messages for this run — some may have stored
  // ciphertext-retaining fallback rows (P1) that the post-event retry heals.
  // Hoisted so the scoped retry pass (P3-c) can target them.
  let mergedChatIds: string[] = [];
  if (shouldFetchNewMessages) {
    mergedChatIds = [
      ...new Set([...changedChatIds, ...previouslyFailedChatIds]),
    ];
    if (mergedChatIds.length > 0) {
      if (windowSet) {
        // WS2 Phase 2a: backfill the window's new messages FIRST, then the rest.
        // Ordering-only — identical total request count, and
        // `failedMessageChatIds` accumulates across both partitions exactly as
        // the single call did (`syncNewMessagesForChats([])` is a no-op).
        const windowFirst = mergedChatIds.filter((id) => windowSet.has(id));
        const remainder = mergedChatIds.filter((id) => !windowSet.has(id));
        const failedWindow = await syncNewMessagesForChats(windowFirst);
        const failedRest = await syncNewMessagesForChats(remainder);
        failedMessageChatIds = [...failedWindow, ...failedRest];
      } else {
        failedMessageChatIds = await syncNewMessagesForChats(mergedChatIds);
      }
    }
  }

  // Bound the retry of failed message fetches. A chat that keeps failing has its
  // attempt count incremented; once it reaches MAX_MESSAGE_FETCH_RETRIES we give
  // up on it (drop it from the retry set) so it can't hold the event cursor
  // forever. `retryableFailedChatIds` are the ones still worth retrying — only
  // those gate the event-cursor advance below.
  const nextFailedAttempts: Record<string, number> = {};
  const retryableFailedChatIds: string[] = [];
  for (const id of failedMessageChatIds) {
    const attempts = (previousFailedAttempts[id] ?? 0) + 1;
    if (attempts < MAX_MESSAGE_FETCH_RETRIES) {
      nextFailedAttempts[id] = attempts;
      retryableFailedChatIds.push(id);
    } else {
      // Cap reached — give up; accept the localised event loss for this chat.
      // eslint-disable-next-line no-console
      console.warn(
        `[syncEngine] Giving up on chat ${id} after ${attempts} failed message fetches`,
      );
    }
  }

  // WS2 Phase 2a: kick preview hydration for the visible window NOW — BEFORE the
  // (potentially multi-minute) event drain below — so the window's last-message
  // previews land in seconds instead of behind the drain (the warm-start T1
  // fix). Only when there is a drain to get ahead of (`shouldFetchEvents`). The
  // end-of-run pass (step 6) still runs to catch tail + drain-produced
  // candidates; the rerun latch keeps it from being a stale no-op. Emits a
  // NO-PII, sampled `window_preview_hydrated` metric (F5: previews were
  // decrypted — NOT that the shown message is necessarily up to date, since the
  // drain may still edit/delete it). Fire-and-forget — never blocks/fails a run.
  if (windowSet && shouldFetchEvents) {
    void lazyDecryptPreviews(windowIds)
      .then((res) => {
        if (
          res.windowHydrationMs !== null &&
          Math.random() < SYNC_RUN_SUMMARY_SAMPLE_RATE
        ) {
          reportSyncTelemetry("window_preview_hydrated", syncRunId, {
            windowSize: windowIds.length,
            windowCandidateCount: res.windowCandidateCount,
            msToWindowPreviewHydrated: res.windowHydrationMs,
            path: syncPath,
          });
        }
      })
      .catch((err) => log.warn("[sync] early window preview kick failed", err));
  }

  // 3. Fetch and apply per-chat events (delta sync only).
  // Runs after new messages so events can reference newly inserted messages.
  // On first sync and fullSync fallback, events are skipped because there are
  // no local messages to apply them to (or we just re-fetched everything).
  // P3-c: chats that had events applied this run — combined with mergedChatIds
  // (the chats we backfilled), this is the SMALL set the post-event retry pass
  // scopes to, instead of a global re-sweep of the whole failed backlog.
  const chatsWithEvents: string[] = [];
  // Set only by the v2 drain: the derived (ISO-normalized min over per-chat
  // positions) value the legacy META_EVENT_CURSOR is written with this run.
  let v2DerivedEventCursor: string | undefined;
  // Set only by the v2 drain: §4.1+§4.2 bucket/count fields attached to the
  // SAMPLED sync_run_summary below (they inherit its 2% sampling — acceptable
  // for fleet percentile metrics; the anomaly-class signals have their own
  // unsampled events). Undefined on the v1 path and on runs with no drain.
  let v2DrainSummary:
    | {
        oldestDrainAgeBucket?: string;
        laggardCount: number;
        maxLaggardAgeBucket?: string;
      }
    | undefined;
  if (shouldFetchEvents && eventChatIds.length > 0) {
    // Defense-in-depth `useNewSync` gate: runSync only executes under the new
    // sync engine, but gate explicitly (same pattern as windowedHydration).
    if (featureFlags.syncEventDrainV2 && featureFlags.useNewSync) {
      // PR-A1 v2 drain: per-chat persisted cursors + mid-run checkpointing +
      // numeric batch-since + starved re-queue + visibility-aware boundary.
      const drainResult = await runEventDrainV2(
        eventChatIds,
        existingEventCursor,
        syncStartTime,
        syncRunId,
        retryableFailedChatIds,
      );
      incompleteEventChatIds.push(...drainResult.incomplete);
      chatsWithEvents.push(...drainResult.withEvents);
      v2DerivedEventCursor = drainResult.derivedLegacyCursor;
      // Absent (undefined) buckets are OMITTED, not passed as undefined keys,
      // so the raw call payload matches the wire payload (scrubData would
      // drop them anyway — this keeps the two layers identical).
      v2DrainSummary = {
        laggardCount: drainResult.laggardCount,
        ...(drainResult.oldestDrainAgeBucket !== undefined && {
          oldestDrainAgeBucket: drainResult.oldestDrainAgeBucket,
        }),
        ...(drainResult.maxLaggardAgeBucket !== undefined && {
          maxLaggardAgeBucket: drainResult.maxLaggardAgeBucket,
        }),
      };
    } else {
      const eventsSince = existingEventCursor ?? syncStartTime;

      // ONE sequential paginator per 100-chat chunk (P2-lite) instead of a
      // ceil(N/5)-way concurrent fan-out. fetchEvents already accepts the array
      // and the BE caps it at 100, so each chunk's page is a single POST that the
      // server fans out — collapsing ceil(N/5) POSTs per page to ceil(N/100).
      // Pacing + 429 backoff are handled inside fetchEvents by the shared sync
      // scheduler; syncEventsForChats narrows to lagging chats via `hasMore`.
      for (let i = 0; i < eventChatIds.length; i += EVENT_CHATIDS_PER_REQUEST) {
        const chunk = eventChatIds.slice(i, i + EVENT_CHATIDS_PER_REQUEST);
        const { incomplete, withEvents } = await syncEventsForChats(
          chunk,
          eventsSince,
        );
        if (incomplete.length > 0) {
          incompleteEventChatIds.push(...incomplete);
        }
        if (withEvents.length > 0) {
          chatsWithEvents.push(...withEvents);
        }
      }
    }

    // Observability for the full-resync event catch-up (Fix 1 follow-up). On a
    // full-resync we replay events from the TTL-clamped floor; chats whose event
    // pages couldn't fully drain this run HOLD the event cursor and re-replay next
    // run. We have no field visibility into how often that tail occurs, which is
    // the signal that decides whether Fix 2 (bounded message-window reconcile for
    // page-capped / >TTL accounts) is worth building. Emit a NO-PII anomaly event
    // (counts only, never chat IDs) when a full-resync leaves chats incomplete.
    // The denominator is the existing `full_resync_completed`. Delta-path runs are
    // excluded (their incomplete handling is already covered by sync_run_summary).
    if (syncPath === "full-resync" && incompleteEventChatIds.length > 0) {
      reportSyncTelemetry("resync_event_catchup_incomplete", syncRunId, {
        incompleteCount: incompleteEventChatIds.length,
        scopedChatCount: eventChatIds.length,
      });
    }

    // Only advance the event cursor if nothing is still worth retrying:
    //  (a) no chat's new-message fetch is pending under the retry cap, AND
    //  (b) every chat's events were fully drained (no incomplete pagination).
    // A chat whose new-message fetch failed (but is under the retry cap), OR
    // whose event pages weren't fully drained, holds the cursor so its
    // edit/delete/reaction events re-apply next round (events are idempotent).
    // Chats that hit the message-retry cap are no longer retryable, so they
    // don't hold the cursor — preventing one permanently-broken chat from
    // stalling event sync for the whole account.
    if (
      retryableFailedChatIds.length === 0 &&
      incompleteEventChatIds.length === 0
    ) {
      newEventCursor = syncStartTime;
    }

    // Kick the decryption-retry orchestrator now that events are applied.
    // `applyEvent`'s `message.edited` branch marks edited rows `failed` (the WS
    // UPDATE path decrypts inline, but the sync path has no plaintext), so
    // without this they'd stay "Decrypting…" until some later trigger. P3-c:
    // scope the pass to the chats that actually CHANGED this run — the ones we
    // backfilled (`mergedChatIds`, may hold ciphertext-retaining fallback rows)
    // plus the ones whose events we applied (`chatsWithEvents`) — instead of a
    // global re-sweep of every failed row in the account on every sync.
    // `eventChatIds` would NOT narrow (it is all local chats) and would force a
    // less-efficient chatId scan over the efficient `decryptionStatus` index.
    // Gated by `featureFlags.decryptionRetry`. Fire-and-forget so it never
    // blocks (or fails) the sync run.
    const retryScope = [...new Set([...mergedChatIds, ...chatsWithEvents])];
    if (retryScope.length > 0) {
      void lazyRunDecryptionRetry(retryScope).catch((err) =>
        log.warn("[sync] post-event decryption retry failed", err),
      );
    }
  }

  // P0-b: gate the chat/stream cursor the SAME way as the event cursor above.
  // Only advance it to the delta's toCursor if this run fully drained — no
  // retryable message-fetch failures AND no incomplete event pages (including a
  // chat whose transactional event-apply aborted). Otherwise it stays held at
  // the previous cursor so the next delta re-includes the chats that didn't
  // drain, instead of silently skipping past partially-applied changes.
  // `deltaToCursor` is null on the first-sync / full-resync paths (which write a
  // complete snapshot and set `newSyncCursor` directly), so this never overrides
  // them.
  if (
    deltaToCursor !== null &&
    retryableFailedChatIds.length === 0 &&
    incompleteEventChatIds.length === 0
  ) {
    newSyncCursor = deltaToCursor;
  }

  // 4. Persist both cursors atomically in a single transaction (HIGH 3).
  // If the process crashes between two separate writes, cursor state splits.
  // Under the v2 drain the legacy event cursor is DERIVED (ISO-normalized
  // numeric min over the per-chat map — laggards hold only themselves back),
  // so it takes precedence over the v1 advance/hold gate when set.
  const finalEventCursor =
    v2DerivedEventCursor ??
    newEventCursor ??
    existingEventCursor ??
    syncStartTime;
  await db.transaction("rw", db.meta, async () => {
    await db.meta.put({ key: META_SYNC_CURSOR, value: newSyncCursor });
    await db.meta.put({ key: META_EVENT_CURSOR, value: finalEventCursor });
    // Persist or clear the still-retryable failed chat IDs + their attempt
    // counts (HIGH 2). Chats that hit the retry cap are dropped from both.
    if (retryableFailedChatIds.length > 0) {
      await db.meta.put({
        key: META_FAILED_MESSAGE_CHATS,
        value: retryableFailedChatIds,
      });
      await db.meta.put({
        key: META_FAILED_MESSAGE_ATTEMPTS,
        value: nextFailedAttempts,
      });
    } else {
      await db.meta.delete(META_FAILED_MESSAGE_CHATS);
      await db.meta.delete(META_FAILED_MESSAGE_ATTEMPTS);
    }
  });

  // 5. Signal that the chat list is ready for display.
  // This replaces the legacy loadChats() → setFinishedLoadingFirstPageOfChatList(true)
  // path that the ChatPage loading screen depends on.
  useChatListState.getState().setFinishedLoadingFirstPageOfChatList(true);

  // 6. Post-sync hydration: decrypt lastMessage previews in background.
  // Non-blocking — chat list shows descriptions until previews decrypt. WS2
  // Phase 2a: pass `windowIds` so this pass also decrypts the visible window
  // first, and (via the rerun latch) catches candidates the early kick's pass
  // missed because the event drain produced them after it built its candidate
  // list.
  lazyDecryptPreviews(windowSet ? windowIds : undefined).catch((err) =>
    log.warn("[sync] preview decryption failed", err),
  );

  // 7. G1 lazy-decrypt: this run's chat-list merge is committed, so every
  // chat's `eCSK` is now current. Re-attempt decryption of ALL pending
  // ciphertext rows from their STORED bytes (NEVER a network refetch) —
  // catching rows whose key arrived but that the scoped post-event retry pass
  // (P3-c) didn't cover (they aren't tracked in `previouslyFailedChatIds`,
  // which only holds chats whose message FETCH threw). Under the new sync
  // engine the global `chatListSyncing` orchestrator trigger never fires, so
  // without this those rows stay "Decrypting…" until the next reconnect/reload.
  // Fire-and-forget so it never blocks (or fails) the sync run.
  lazyRunLazyDecryptPending().catch((err) =>
    log.warn("[sync] G1 lazy-decrypt pass failed", err),
  );

  // 8. sync_run_summary (SAMPLED ~2%) — the routine success path. We do NOT
  // emit one per run (that would be the bulk of telemetry volume and would burn
  // the daily cap); the anomaly events above carry the numerator truth, and this
  // sampled counter gives a denominator for "how many runs total". The dedupe +
  // daily-cap guards still bound it; sampling keeps it from dominating the cap on
  // a chatty client. Outcome is always "ok" here (failures throw before reaching
  // this point and surface as scheduler_timeout / incomplete events).
  if (Math.random() < SYNC_RUN_SUMMARY_SAMPLE_RATE) {
    reportSyncTelemetry("sync_run_summary", syncRunId, {
      path: syncPath,
      outcome: "ok",
      durationMs: Date.now() - runStartedAt,
      localChatCount: syncPath === "first" ? 0 : summaryChatCount,
      // §4.4 drainVersion — the ONE field deliberately added on BOTH paths so
      // ramp-phase comparisons can split engines. Mirrors the drain gate above.
      drainVersion:
        featureFlags.syncEventDrainV2 && featureFlags.useNewSync ? "v2" : "v1",
      // §4.1+§4.2 (v2 delta runs only; undefined values are scrubbed) —
      // buckets/counts, NEVER raw ages/timestamps/ids.
      ...(v2DrainSummary ?? {}),
    });
  }
}
